import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
  ImperativePanelHandle,
} from "react-resizable-panels";
import { motion } from "framer-motion";
import {
  EyeIcon,
  CodeBracketIcon,
  DocumentDuplicateIcon,
  PencilIcon,
  ShareIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  ChevronRightIcon,
  BookmarkIcon,
  PlayIcon,
  BookOpenIcon,
  DocumentTextIcon,
  SparklesIcon,
  ChatBubbleLeftRightIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import BasicMenu from "@/components/menu";
import MenuItem from "@mui/material/MenuItem";

import { useDocumentStore } from "@stores/documentStore";
import { useEditorStore } from "@stores/editorStore";
import { useThemeStore } from "@stores/themeStore";
import { documentsAPI } from "@api/documents";
import ScientificEditor from "@components/editor/ScientificEditor";
import WordEditor from "@components/editor/WordEditor";
import DocumentPreview from "@components/editor/DocumentPreview";
import LatexCompilePanel from "@components/editor/LatexCompilePanel";
import ErrorBoundary from "@components/common/ErrorBoundary";
import ShareDocumentModal from "@components/document/ShareDocumentModal";
import { useDocumentPermission } from "@hooks/useDocumentPermission";
import { useCollaboration } from "@hooks/useCollaboration";
import { useAuthStore } from "@stores/authStore";
import { documentSharesAPI } from "@api/documentShares";
import SharedDocumentPrompt from "@components/common/SharedDocumentPrompt";
import { UserPresenceIndicator } from "@components/collaboration/UserPresenceIndicator";
import { FileBrowser } from "@components/latex/FileBrowser";
import { TabBar, type Tab } from "@components/latex/TabBar";
import { latexAPI, LatexCompilationError } from "@api/latex";
import LaTeXDebugPanel from "@components/editor/LaTeXDebugPanel";
import { CitationLibraryPanel } from "@components/citations/CitationLibraryPanel";
import { LiteratureDiscoveryPanel } from "@components/research/LiteratureDiscoveryPanel";
import { DraftGenerator } from "@components/research/DraftGenerator";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { aiProvidersAPI } from "@api/aiProviders";
import { tokenUsageAPI, type TokenUsageStats } from "@api/tokenUsage";
import { logError, logWarn, logDebug } from "@utils/logger";
import { generateBibtexEntry } from "@utils/generateBibtex";
import LaTeXToolbar from "@components/editor/latex-toolbar/LaTeXToolbar";
import {
  detectBibliographyType,
  generateBibitemEntry,
  insertBibitemIntoDocument,
} from "@utils/latexBibliography";
import { clearCitationCache } from "@utils/citationAutocomplete";
import type { DocumentFile } from "../../types";
import type { CitationPaper } from "@api/citations";
import { EditorView } from "@codemirror/view";
import ReviewSidebar from "../review/ReviewSidebar";
import { ReviewProvider, useReviewStore } from "@stores/reviewStore";
import { parseLatexWarning } from "@utils/parseLatexWarning";

type ViewMode = "editor" | "split" | "preview";
type SidePanel = "latex" | "citations" | "literature" | "draft" | "review" | null;

const computeContentHash = (value: string): string => {
  if (!value) return "0";
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0; // Convert to 32bit integer
  }
  return hash.toString(36);
};

/**
 * Deduplicate array of strings, preserving order (first occurrence kept)
 * Normalizes whitespace for comparison but preserves original formatting
 */
const deduplicateStrings = (arr: string[]): string[] => {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const item of arr) {
    // Normalize whitespace for comparison (trim and collapse multiple spaces)
    const normalized = item.trim().replace(/\s+/g, ' ');
    if (!seen.has(normalized)) {
      seen.add(normalized);
      unique.push(item); // Keep original formatting
    }
  }
  return unique;
};

const EditorPage: React.FC = () => {
  const { documentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Set default view mode based on document type
  const [viewMode, setViewMode] = useState<ViewMode>("editor");
  const [hasSetDefaultViewMode, setHasSetDefaultViewMode] = useState(false);
  const [sidePanel, setSidePanel] = useState<SidePanel>(null);
  const [isWaitingForZipImport, setIsWaitingForZipImport] = useState(false);
  const [pendingShare, setPendingShare] = useState<{ email: string } | null>(null);
  const { user, isAuthenticated } = useAuthStore();
  const [aiProvider, setAiProvider] = useState<string | null>(null);
  const [aiModel, setAiModel] = useState<string | null>(null);
  const [isAIMenuOpen, setIsAIMenuOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number } | null>(null);
  const aiModelButtonRef = useRef<HTMLButtonElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Update dropdown position when it opens
  useEffect(() => {
    if (isAIMenuOpen && aiModelButtonRef.current) {
      const updatePosition = () => {
        if (aiModelButtonRef.current) {
          const rect = aiModelButtonRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY + 8,
            right: window.innerWidth - rect.right
          });
        }
      };
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    } else {
      setDropdownPosition(null);
    }
  }, [isAIMenuOpen]);

  // Track screen size for responsive layout
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024
      setIsMobile(mobile)
      // On mobile, default to editor-only view
      if (mobile && viewMode === 'split') {
        setViewMode('editor')
      }
    }
    window.addEventListener('resize', handleResize)
    handleResize() // Initial check
    return () => window.removeEventListener('resize', handleResize)
  }, [viewMode])

  const {
    currentDocument,
    setCurrentDocument,
    updateDocument,
    isSaving,
    triggerAutosave,
  } = useDocumentStore();


  // Get editor store - with safety check for provider availability
  let editorStore;
  try {
    editorStore = useEditorStore();
  } catch (error) {
    // Provider not available - this shouldn't happen in production
    console.error("EditorProvider not available", error);
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-gray-600">
            Editor not available. Please refresh the page.
          </p>
        </div>
      </div>
    );
  }

  const {
    content,
    setContent,
    mode,
    setMode,
    isDirty,
    markClean,
    selection,
    cursorPosition,
  } = editorStore;

  // Review store for comments, history, and track changes
  const reviewStore = useReviewStore();
  const [trackChangesEnabled, setTrackChangesEnabled] = useState(false);

  // Set split view as default for LaTeX documents (only once on initial load)
  useEffect(() => {
    if (
      currentDocument?.document_type === "latex" &&
      !hasSetDefaultViewMode &&
      viewMode === "editor"
    ) {
      setViewMode("split");
      setHasSetDefaultViewMode(true);
    }
    // Force editor-only mode for Word documents
    if (currentDocument?.document_type === "docx" && viewMode !== "editor") {
      setViewMode("editor");
    }
  }, [currentDocument?.document_type, hasSetDefaultViewMode, viewMode]);

  // Reset default view mode flag when document changes
  useEffect(() => {
    if (documentId) {
      setHasSetDefaultViewMode(false);
    }
  }, [documentId]);

  // Redirect to documents page if no document is open
  useEffect(() => {
    if (!documentId && !currentDocument) {
      // No document ID in URL and no current document - redirect to documents page
      navigate("/documents");
    }
  }, [documentId, currentDocument, navigate]);

  // Subscribe to theme changes for reactive updates
  const effectiveTheme = useThemeStore((state) => state.effectiveTheme);

  // Set editor mode based on document type
  useEffect(() => {
    if (currentDocument?.document_type) {
      const docType = currentDocument.document_type;
      if (docType === "latex" && mode !== "latex") {
        setMode("latex");
      } else if (docType === "markdown" && mode !== "markdown") {
        setMode("markdown");
      } else if (docType === "text" && mode !== "plain") {
        setMode("plain");
      } else if (docType === "docx" && mode !== "word") {
        setMode("word");
      }
    }
  }, [currentDocument?.document_type, mode, setMode]);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState("");
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [blameEnabled, setBlameEnabled] = useState(false); // Git blame toggle state

  // Load file browser state from localStorage on mount
  const getInitialFileBrowserState = () => {
    try {
      const saved = localStorage.getItem("fileBrowser_show");
      const savedCollapsed = localStorage.getItem("fileBrowser_collapsed");
      if (saved !== null) {
        return {
          show: saved === "true",
          collapsed: savedCollapsed === "true",
        };
      }
    } catch (error) {
      logWarn(
        "[EditorPage] Failed to load file browser state from localStorage:",
        error
      );
    }
    return null;
  };

  const initialFileBrowserState = getInitialFileBrowserState();
  const [showFileBrowser, setShowFileBrowser] = useState(
    initialFileBrowserState?.show ?? false
  ); // File browser visibility
  const [isFileBrowserCollapsed, setIsFileBrowserCollapsed] = useState(
    initialFileBrowserState?.collapsed ?? false
  ); // File browser collapse state
  // Tab management state
  const [openTabs, setOpenTabs] = useState<Tab[]>([]); // Array of open tabs
  const [activeTabId, setActiveTabId] = useState<string | null>(null); // ID of currently active tab
  const [fileBrowserRefreshToken, setFileBrowserRefreshToken] = useState(0);
  const [isRefreshingEditor, setIsRefreshingEditor] = useState(false);
  const [isExporting, setIsExporting] = useState(false); // Export in progress
  const [isCompiling, setIsCompiling] = useState(false); // Compilation in progress
  const [isLoadingDocument, setIsLoadingDocument] = useState(false); // Document loading in progress
  const [compiledPdfBlob, setCompiledPdfBlob] = useState<Blob | null>(null); // Store compiled PDF blob from handleCompile
  const [compilationErrors, setCompilationErrors] = useState<string[]>([]); // Compilation errors
  const [compilationWarnings, setCompilationWarnings] = useState<string[]>([]); // Compilation warnings
  const [showDebugPanel, setShowDebugPanel] = useState(false); // Debug panel visibility
  const [hasFatalErrors, setHasFatalErrors] = useState(false); // True if compilation has fatal errors
  const [highlightLine, setHighlightLine] = useState<{ lineNumber: number; type: 'warning' | 'error' } | null>(null); // Line to highlight
  const [navigateToPdfPage, setNavigateToPdfPage] = useState<number | null>(null); // PDF page to navigate to
  const fileBrowserPanelRef = useRef<ImperativePanelHandle>(null);
  const editorPanelRef = useRef<ImperativePanelHandle>(null);
  const compiledPdfBlobRef = useRef<Blob | null>(null);
  const compiledContentHashRef = useRef<string | null>(null);
  const compiledPdfFilePathRef = useRef<string | null>(null); // Track which file the PDF was compiled for
  const initialAutoCompileAttemptedRef = useRef(false);
  const selectedFilePathRef = useRef<string | null>(null); // Track current file path to avoid stale state
  const lastTexContentHashRef = useRef<string | null>(null); // Track the hash of the last .tex file content we viewed
  const fileBrowserInitializedRef = useRef(false); // Track if file browser state has been initialized for current document type
  const fileSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingFileSaveRef = useRef<{ fileId: string; content: string } | null>(
    null
  );
  const [isSavingFile, setIsSavingFile] = useState(false);
  const [isPdfStale, setIsPdfStale] = useState(false);
  // True when the currently shown PDF was compiled for a different .tex file
  const [isPdfForDifferentFile, setIsPdfForDifferentFile] = useState(false);
  const [editorView, setEditorView] = useState<EditorView | null>(null);
  const currentEditorContentRef = useRef<string>(content);
  const isSwitchingTabsRef = useRef<boolean>(false); // Flag to prevent content updates during tab switch
  const expectedContentOnSwitchRef = useRef<string | null>(null); // Track expected content when switching tabs

  // Update ref whenever content changes
  useEffect(() => {
    currentEditorContentRef.current = content;
  }, [content]);

  // File save functions (must be defined before tab management functions that use them)
  const flushPendingFileSave = useCallback(
    async (options?: { silent?: boolean; forceVersion?: boolean }) => {
      if (fileSaveTimeoutRef.current) {
        clearTimeout(fileSaveTimeoutRef.current);
        fileSaveTimeoutRef.current = null;
      }
      if (!pendingFileSaveRef.current || !currentDocument?.id) {
        return true;
      }
      const silent = options?.silent ?? false;
      const forceVersion = options?.forceVersion ?? false;
      const payload = pendingFileSaveRef.current;
      pendingFileSaveRef.current = null;
      setIsSavingFile(true);
      try {
        await latexAPI.updateFile(currentDocument.id, payload.fileId, {
          content: payload.content,
        }, forceVersion);
        logDebug("[EditorPage] Saved pending file content", {
          fileId: payload.fileId,
          forceVersion,
        });
        return true;
      } catch (error: any) {
        logError("[EditorPage] Failed to save file content:", error);
        // Re-queue so user can retry
        pendingFileSaveRef.current = payload;
        return false;
      } finally {
        setIsSavingFile(false);
      }
    },
    [currentDocument?.id]
  );

  // Tab management helper functions
  // CRITICAL: getActiveTab() should never return null if a file is open
  // If there are tabs but no activeTabId, use the first tab
  // This ensures every open file has a corresponding tab
  const getActiveTab = useCallback((): Tab | null => {
    // If we have an activeTabId, find that tab
    if (activeTabId) {
      const tab = openTabs.find(tab => tab.file.id === activeTabId);
      if (tab) return tab;
      // Tab ID doesn't match - might be stale, fall through to use first tab
    }
    
    // If we have tabs but no active tab ID (or ID doesn't match), use first tab
    // This ensures getActiveTab() returns a tab when tabs exist
    if (openTabs.length > 0) {
      const firstTab = openTabs[0];
      // Auto-set the active tab ID if it's not set (fixes state inconsistency)
      if (!activeTabId && firstTab) {
        logDebug("[EditorPage] Auto-setting activeTabId to first tab", {
          tabName: firstTab.file.name,
          tabCount: openTabs.length
        });
        setActiveTabId(firstTab.file.id);
        selectedFilePathRef.current = firstTab.file.file_path;
      }
      return firstTab;
    }
    
    return null;
  }, [openTabs, activeTabId]);

  const addOrSwitchToTab = useCallback(async (file: DocumentFile, content?: string) => {
    // Use functional state update to check latest state and prevent duplicates
    let shouldCreateTab = false;
    let existingTab: Tab | undefined;
    
    setOpenTabs(prev => {
      // Check if tab already exists in the latest state
      existingTab = prev.find(tab => tab.file.id === file.id);
      shouldCreateTab = !existingTab;
      return prev; // Don't modify state here
    });

    if (existingTab) {
      // Tab exists - switch to it
      setActiveTabId(file.id);
      selectedFilePathRef.current = file.file_path;
      setContent(existingTab.content);
      markClean();
      return;
    }

    // Load content if not provided
    let fileContent = content;
    if (!fileContent && currentDocument?.id) {
      try {
        const blob = await latexAPI.getFileContent(currentDocument.id, file.id);
        fileContent = await blob.text();
      } catch (error: any) {
        logError("[EditorPage] Failed to load file content for tab:", error);
        fileContent = "";
      }
    }

    // Create new tab - use functional update to prevent duplicates
    const newTab: Tab = {
      file,
      content: fileContent || "",
      isDirty: false,
      lastSavedContent: fileContent || "",
    };

    setOpenTabs(prev => {
      // Double-check tab doesn't exist (race condition protection)
      if (prev.find(t => t.file.id === file.id)) {
        // Tab was added by another call - switch to it instead
        const tab = prev.find(t => t.file.id === file.id)!;
        setActiveTabId(file.id);
        selectedFilePathRef.current = file.file_path;
        setContent(tab.content);
        markClean();
        return prev;
      }
      return [...prev, newTab];
    });
    
    setActiveTabId(file.id);
    selectedFilePathRef.current = file.file_path;
    setContent(newTab.content);
    markClean();
  }, [currentDocument?.id, setContent, markClean]);

  const switchToTab = useCallback((fileId: string) => {
    const tab = openTabs.find(t => t.file.id === fileId);
    if (!tab) return;

    // Set flag to prevent content updates during switch
    isSwitchingTabsRef.current = true;

    // Save current tab's content before switching (use ref to get latest content)
    const currentTab = getActiveTab();
    const isSwitchingToDifferentTexFile =
      !!currentTab &&
      currentTab.file.id !== fileId &&
      // Only treat it as a LaTeX main-file switch if BOTH tabs are .tex files
      currentTab.file.file_path.endsWith(".tex") &&
      tab.file.file_path.endsWith(".tex") &&
      currentTab.file.file_path !== tab.file.file_path;

    if (currentTab && currentTab.file.id !== fileId) {
      const currentContent = currentEditorContentRef.current;
      // Update current tab's content from editor before switching
      setOpenTabs(prev => prev.map(t => 
        t.file.id === currentTab.file.id 
          ? { ...t, content: currentContent, isDirty: currentContent !== t.lastSavedContent }
          : t
      ));
    }

    // CRITICAL: When switching to a different .tex file, DO NOT clear the PDF.
    // Instead, keep it visible but mark it as stale/for-different-file.
    // The useEffect will handle marking it appropriately, and the user can manually compile.
    if (isSwitchingToDifferentTexFile) {
      logDebug('[EditorPage] Switching to different .tex file, keeping PDF but marking as stale', {
        from: currentTab?.file.file_path,
        to: tab.file.file_path,
        compiledFor: compiledPdfFilePathRef.current
      });
      // Clear compilation errors/warnings when switching files (they're file-specific)
      setCompilationErrors([]);
      setCompilationWarnings([]);
      setHasFatalErrors(false);
      // Do NOT reset auto-compile flag - we don't want to auto-compile when switching tabs
      // The PDF will be marked as stale/for-different-file by the useEffect
    }

    setActiveTabId(fileId);
    selectedFilePathRef.current = tab.file.file_path;
    setContent(tab.content);
    markClean();

    // Clear flag after a short delay to allow editor to update
    setTimeout(() => {
      isSwitchingTabsRef.current = false;
    }, 100);
  }, [openTabs, getActiveTab, setContent, markClean]);

  const closeTab = useCallback(async (fileId: string) => {
    const tabToClose = openTabs.find(t => t.file.id === fileId);
    if (!tabToClose) return;

    // Save current tab's content if it's the one being closed
    if (activeTabId === fileId) {
      const currentContent = currentEditorContentRef.current;
      setOpenTabs(prev => prev.map(t => 
        t.file.id === fileId 
          ? { ...t, content: currentContent, isDirty: currentContent !== t.lastSavedContent }
          : t
      ));

      // Auto-save if dirty
      if (tabToClose.isDirty || currentContent !== tabToClose.lastSavedContent) {
        await flushPendingFileSave({ silent: true });
      }
    }

    // Remove tab
    const newTabs = openTabs.filter(t => t.file.id !== fileId);
    setOpenTabs(newTabs);

    // Switch to another tab if we closed the active one
    if (activeTabId === fileId) {
      if (newTabs.length > 0) {
        // Switch to the previous tab, or first tab if it was the first
        const closedIndex = openTabs.findIndex(t => t.file.id === fileId);
        const newActiveIndex = closedIndex > 0 ? closedIndex - 1 : 0;
        switchToTab(newTabs[newActiveIndex].file.id);
      } else {
        // No tabs left, clear editor
        setActiveTabId(null);
        selectedFilePathRef.current = null;
        setContent("");
        markClean();
      }
    }
  }, [openTabs, activeTabId, flushPendingFileSave, switchToTab, setContent, markClean]);

  const updateActiveTabContent = useCallback((newContent: string) => {
    if (!activeTabId) return;
    
    // Don't update if we're in the middle of switching tabs
    if (isSwitchingTabsRef.current) {
      return;
    }
    
    setOpenTabs(prev => prev.map(tab => 
      tab.file.id === activeTabId
        ? { ...tab, content: newContent, isDirty: newContent !== tab.lastSavedContent }
        : tab
    ));
  }, [activeTabId]);

  useEffect(() => {
    // CRITICAL: Clear PDF blob IMMEDIATELY when documentId changes
    // This prevents DocumentPreview from showing the previous document's PDF
    // We clear both state and ref synchronously to ensure immediate effect
    setCompiledPdfBlob(null);
    compiledPdfBlobRef.current = null;
    compiledContentHashRef.current = null;
    compiledPdfFilePathRef.current = null;
    lastTexContentHashRef.current = null; // Reset when switching documents
    setIsPdfStale(false);
    setIsPdfForDifferentFile(false);
    // Clear compilation errors/warnings when switching documents
    setCompilationErrors([]);
    setCompilationWarnings([]);
    setHasFatalErrors(false);
    setShowDebugPanel(false);
    initialAutoCompileAttemptedRef.current = false;
    // Clear tabs when switching documents
    setOpenTabs([]);
    setActiveTabId(null);
    selectedFilePathRef.current = null;
    logDebug("[EditorPage] Cleared PDF blob and state for new document", { documentId });
  }, [documentId]);

  // Get user's AI preferences (provider/model/latex compiler)
  const { data: aiPreferences } = useQuery({
    queryKey: ["aiPreferences"],
    queryFn: () => aiProvidersAPI.getPreferences(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Get user-level daily AI credit stats for header indicator
  const { data: userTokenStats } = useQuery<TokenUsageStats>({
    queryKey: ["token-usage", "user-stats"],
    queryFn: () => tokenUsageAPI.getUserStats(),
    staleTime: 60 * 1000,
  });

  // Sync local AI selection state with preferences
  useEffect(() => {
    if (aiPreferences) {
      setAiProvider(aiPreferences.provider || null);
      setAiModel(aiPreferences.model || null);
    }
  }, [aiPreferences]);

  // Fetch token usage for current document (only on mount, no auto-refetch)
  const { data: documentTokenUsage } = useQuery({
    queryKey: ["token-usage", "document", currentDocument?.id],
    queryFn: () =>
      currentDocument?.id
        ? tokenUsageAPI.getDocumentUsage(currentDocument.id)
        : null,
    enabled: !!currentDocument?.id,
    // No refetchInterval - will only refetch when invalidated after AI operations
  });

  // Track remote user cursors for display in editor
  const [remoteCursors, setRemoteCursors] = useState<
    Array<{
      userId: string;
      username: string;
      full_name?: string;
      position: number;
      color: string;
    }>
  >([]);
  useEffect(() => {
    compiledPdfBlobRef.current = compiledPdfBlob;
  }, [compiledPdfBlob]);
  const removePersistedPdfForDocument = useCallback((docId: string, mainFilePath?: string | null) => {
    try {
      // Remove both new format (scoped by mainFilePath) and old format (for backward compatibility)
      if (mainFilePath) {
        const safeMainPath = mainFilePath.replace(/[^a-zA-Z0-9_\-./]/g, "_");
        const newFormatKey = `pdf_blob_${docId}_${safeMainPath}`;
        localStorage.removeItem(newFormatKey);
        localStorage.removeItem(`${newFormatKey}_timestamp`);
        localStorage.removeItem(`${newFormatKey}_hash`);
      }
      // Also remove old format keys (for backward compatibility)
      const oldFormatKey = `pdf_blob_${docId}`;
      localStorage.removeItem(oldFormatKey);
      localStorage.removeItem(`${oldFormatKey}_timestamp`);
      localStorage.removeItem(`${oldFormatKey}_hash`);
      logDebug("[EditorPage] Removed persisted PDF blob", { docId, mainFilePath });
    } catch (error) {
      logError("[EditorPage] Failed to remove persisted PDF blob:", error);
    }
  }, []);

  const markPdfNeedsRecompile = useCallback(
    (
      reason: string,
      options?: { clearPreview?: boolean; removeCache?: boolean }
    ) => {
      if (!currentDocument?.id || currentDocument.document_type !== "latex")
        return;
      if (!isPdfStale) {
        console.log("[EditorPage] Marking PDF stale", {
          reason,
          documentId: currentDocument.id,
        });
      }
      setIsPdfStale(true);
      // This path is for "same file but content changed", not for file switches
      setIsPdfForDifferentFile(false);
      if (options?.clearPreview) {
        setCompiledPdfBlob(null);
        compiledPdfBlobRef.current = null;
        compiledPdfFilePathRef.current = null;
      }
      if (options?.removeCache) {
        removePersistedPdfForDocument(currentDocument.id);
        compiledContentHashRef.current = null;
      }
    },
    [
      currentDocument?.id,
      currentDocument?.document_type,
      isPdfStale,
      removePersistedPdfForDocument,
    ]
  );

  const triggerFileBrowserRefresh = useCallback(() => {
    setFileBrowserRefreshToken((prev) => prev + 1);
  }, []);

  const scheduleFileSave = useCallback(
    (file: DocumentFile, contentToSave: string) => {
      if (!currentDocument?.id) return;
      pendingFileSaveRef.current = { fileId: file.id, content: contentToSave };
      if (fileSaveTimeoutRef.current) {
        clearTimeout(fileSaveTimeoutRef.current);
      }
      fileSaveTimeoutRef.current = setTimeout(() => {
        flushPendingFileSave({ silent: true }).catch(() => {
          // Errors are handled inside flushPendingFileSave
        });
      }, 1200) as unknown as NodeJS.Timeout;
    },
    [currentDocument?.id, flushPendingFileSave]
  );

  const insertBibliographyCommandsLocally = useCallback(
    (baseContent: string, bibFileName: string): string | null => {
      const endDocumentMatch = baseContent.match(/\\end\{document\}/i);
      if (!endDocumentMatch || endDocumentMatch.index === undefined) {
        return null;
      }
      const insertPosition = endDocumentMatch.index;
      const bibNameWithoutExt = bibFileName.replace(/\.bib$/i, "");
      const commands = `\n% Bibliography\n\\bibliographystyle{unsrt}\n\\bibliography{${bibNameWithoutExt}}\n`;
      return (
        baseContent.slice(0, insertPosition) +
        commands +
        baseContent.slice(insertPosition)
      );
    },
    []
  );

  const refreshActiveFileContent = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!currentDocument?.id) return;
      const silent = options?.silent ?? false;
      try {
        const activeTab = getActiveTab();
        if (activeTab) {
          const blob = await latexAPI.getFileContent(
            currentDocument.id,
            activeTab.file.id
          );
          const text = await blob.text();
          setContent(text);
          markClean();
          
          // Update tab content
          setOpenTabs(prev => prev.map(tab => 
            tab.file.id === activeTab.file.id
              ? { ...tab, content: text, lastSavedContent: text, isDirty: false }
              : tab
          ));

          // Update document content for any .tex file (no special handling for main.tex)
          // This ensures document metadata stays in sync with the active file
          if (activeTab.file.file_path.endsWith(".tex")) {
            setCurrentDocument({
              ...currentDocument,
              content: text,
              word_count: text.split(/\s+/).filter((w) => w.length > 0).length,
              character_count: text.length,
            });
          }
          if (activeTab.file.file_path.endsWith(".tex")) {
            lastTexContentHashRef.current = computeContentHash(text);
          }
        } else if (currentDocument.document_type !== "latex") {
          const latestDocument = await documentsAPI.getById(currentDocument.id);
          setCurrentDocument(latestDocument);
          setContent(latestDocument.content);
          markClean();
        }
      } catch (error: any) {
        logError("[EditorPage] Failed to refresh file content:", error);
      }
    },
    [currentDocument, getActiveTab, setContent, markClean, setCurrentDocument]
  );

  useEffect(() => {
    return () => {
      flushPendingFileSave({ silent: true }).catch(() => {
        // Ignore cleanup errors
      });
    };
  }, [flushPendingFileSave]);

  // Prevent auto-compilation when waiting for ZIP import
  useEffect(() => {
    if (isWaitingForZipImport && currentDocument?.document_type === "latex") {
      // Mark PDF as stale to prevent auto-compilation during ZIP import
      setIsPdfStale(true);
    }
  }, [isWaitingForZipImport, currentDocument?.document_type]);

  const handleManualRefresh = useCallback(async () => {
    if (!currentDocument?.id) return;
    setIsRefreshingEditor(true);
    try {
      const saved = await flushPendingFileSave();
      if (saved === false) {
        throw new Error("Failed to save pending file changes before refresh");
      }
      triggerFileBrowserRefresh();
      await refreshActiveFileContent();
    } catch (error) {
      logError("[EditorPage] Manual refresh failed:", error);
    } finally {
      setIsRefreshingEditor(false);
    }
  }, [
    currentDocument?.id,
    flushPendingFileSave,
    refreshActiveFileContent,
    triggerFileBrowserRefresh,
  ]);

  useEffect(() => {
    if (!documentId) return;
    flushPendingFileSave({ silent: true }).catch(() => {
      // Ignore errors during document switch
    });
  }, [documentId, flushPendingFileSave]);

  const restorePersistedPdfBlob = useCallback(
    async (
      docId: string,
      documentUpdatedAt?: string | null,
      expectedContentHash?: string | null,
      mainFilePath?: string | null
    ) => {
      try {
        // Use new scoped key format: pdf_blob_${docId}_${mainFilePath}
        // Fallback to old format for backward compatibility
        const safeMainPath = (mainFilePath || "default").replace(
          /[^a-zA-Z0-9_\-./]/g,
          "_"
        );
        const newFormatKey = `pdf_blob_${docId}_${safeMainPath}`;
        const oldFormatKey = `pdf_blob_${docId}`;
        
        // Try new format first, then fallback to old format
        let storedPdfBase64 = localStorage.getItem(newFormatKey);
        let storedTimestamp = localStorage.getItem(`${newFormatKey}_timestamp`);
        let storedContentHash = localStorage.getItem(`${newFormatKey}_hash`);
        let baseKey = newFormatKey;
        
        if (!storedPdfBase64 || !storedTimestamp) {
          // Fallback to old format for backward compatibility
          storedPdfBase64 = localStorage.getItem(oldFormatKey);
          storedTimestamp = localStorage.getItem(`${oldFormatKey}_timestamp`);
          storedContentHash = localStorage.getItem(`${oldFormatKey}_hash`);
          baseKey = oldFormatKey;
        }
        
        if (!storedPdfBase64 || !storedTimestamp) {
          return null;
        }

        if (expectedContentHash) {
          if (!storedContentHash) {
            logDebug(
              "[EditorPage] Cached PDF missing content hash, skipping restore",
              { docId, mainFilePath }
            );
            return null;
          }
          if (storedContentHash !== expectedContentHash) {
            logDebug(
              "[EditorPage] Cached PDF hash mismatch, skipping restore",
              {
                docId,
                mainFilePath,
                expectedContentHash,
                storedContentHash,
              }
            );
            return null;
          }
        }

        const compiledAt = parseInt(storedTimestamp, 10);
        const maxAge = 60 * 60 * 1000; // 1 hour

        if (Number.isNaN(compiledAt) || Date.now() - compiledAt > maxAge) {
          logDebug("[EditorPage] Persisted PDF is too old, clearing it", {
            docId,
            mainFilePath,
          });
          localStorage.removeItem(baseKey);
          localStorage.removeItem(`${baseKey}_timestamp`);
          localStorage.removeItem(`${baseKey}_hash`);
          return null;
        }

        const response = await fetch(storedPdfBase64);
        const blob = await response.blob();

        if (documentUpdatedAt) {
          const documentUpdateTime = new Date(documentUpdatedAt).getTime();
          if (
            !Number.isNaN(documentUpdateTime) &&
            documentUpdateTime > compiledAt
          ) {
            logDebug(
              "[EditorPage] Document was edited after PDF compilation, clearing persisted PDF",
              { docId, mainFilePath }
            );
            removePersistedPdfForDocument(docId, mainFilePath);
            return null;
          }
        }

        logDebug("[EditorPage] Restored persisted PDF blob from localStorage", {
          blobSize: blob.size,
          blobType: blob.type,
          docId,
          mainFilePath,
          keyFormat: baseKey === newFormatKey ? "new" : "old",
        });

        return blob;
      } catch (error) {
        logError("[EditorPage] Failed to restore PDF blob:", error);
        return null;
      }
    },
    [removePersistedPdfForDocument]
  );

  // Generate consistent color for each user based on their ID
  const getUserColor = useCallback((userId: string): string => {
    const colors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-purple-500",
      "bg-pink-500",
      "bg-yellow-500",
      "bg-indigo-500",
      "bg-red-500",
      "bg-teal-500",
    ];
    // Use hash of user ID to pick color
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }, []);

  // Get user's permission level for the current document
  const { permission, canEdit } = useDocumentPermission(currentDocument);

  // Get current user for presence indicators
  const { user: currentUser } = useAuthStore();

  // Initialize theme on mount - use effectiveTheme
  useEffect(() => {
    const { effectiveTheme } = useThemeStore.getState();
    if (effectiveTheme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, []);

  // Listen for theme changes
  useEffect(() => {
    const unsubscribe = useThemeStore.subscribe((state) => {
      if (state.effectiveTheme === "dark") {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    });
    return unsubscribe;
  }, []);

  // Persist file browser state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem("fileBrowser_show", showFileBrowser.toString());
      localStorage.setItem(
        "fileBrowser_collapsed",
        isFileBrowserCollapsed.toString()
      );
    } catch (error) {
      logWarn(
        "[EditorPage] Failed to save file browser state to localStorage:",
        error
      );
    }
  }, [showFileBrowser, isFileBrowserCollapsed]);

  // Auto-show file browser for LaTeX documents only if no saved preference exists
  // This runs once when document type changes, respecting saved preferences
  useEffect(() => {
    if (!currentDocument?.document_type) return;

    // Reset initialization flag when document type changes
    fileBrowserInitializedRef.current = false;

    if (currentDocument.document_type === "latex") {
      // Check if there's a saved preference
      try {
        const saved = localStorage.getItem("fileBrowser_show");
        // Only auto-show if there's no saved preference (first time user opens LaTeX document)
        if (saved === null) {
          setShowFileBrowser(true);
          setIsFileBrowserCollapsed(false);
        }
        // If saved preference exists, the state is already set from useState initial value
      } catch (error) {
        // If localStorage fails, default to showing for LaTeX
        setShowFileBrowser(true);
        setIsFileBrowserCollapsed(false);
      }
      fileBrowserInitializedRef.current = true;
    } else {
      // For non-LaTeX documents, always hide (no need to persist this)
      setShowFileBrowser(false);
      setIsFileBrowserCollapsed(true);
      fileBrowserInitializedRef.current = true;
    }
  }, [currentDocument?.document_type]); // Only run when document type changes

  // Enforce file browser panel maxSize constraint (never exceed 20%)
  useEffect(() => {
    if (
      !fileBrowserPanelRef.current ||
      !currentDocument ||
      currentDocument.document_type !== "latex"
    )
      return;

    const enforceMaxSize = () => {
      try {
        if (!fileBrowserPanelRef.current) return;
        const size = fileBrowserPanelRef.current.getSize();
        if (size !== undefined && size > 20) {
          // Force resize to max if it exceeds limit
          fileBrowserPanelRef.current.resize(20);
          logWarn(
            "[EditorPage] File browser panel exceeded maxSize, resizing to 20%"
          );
        }
      } catch (error) {
        // Panel might not be ready yet
      }
    };

    // Check immediately
    enforceMaxSize();

    // Monitor panel size and enforce constraint
    const interval = setInterval(() => {
      enforceMaxSize();

      // Also track collapse state
      try {
        const size = fileBrowserPanelRef.current?.getSize();
        if (size !== undefined) {
          if (size === 0 && showFileBrowser) {
            // Panel was collapsed by dragging
            setShowFileBrowser(false);
            setIsFileBrowserCollapsed(true);
          } else if (size > 0 && !showFileBrowser && !isFileBrowserCollapsed) {
            // Panel was expanded
            setShowFileBrowser(true);
            setIsFileBrowserCollapsed(false);
          }
        }
      } catch (error) {
        // Panel might not be ready yet
      }
    }, 100); // Check every 100ms

    return () => clearInterval(interval);
  }, [showFileBrowser, isFileBrowserCollapsed, currentDocument]);

  // Ensure proper initialization on mount/refresh
  useEffect(() => {
    if (
      !fileBrowserPanelRef.current ||
      !currentDocument ||
      currentDocument.document_type !== "latex"
    )
      return;

    // Small delay to ensure panel is mounted
    const timeout = setTimeout(() => {
      if (!fileBrowserPanelRef.current) return;
      if (showFileBrowser) {
        // Ensure file browser is at correct size (12% default, max 20%)
        const currentSize = fileBrowserPanelRef.current.getSize();
        if (currentSize !== undefined) {
          if (currentSize > 20) {
            fileBrowserPanelRef.current.resize(20);
          } else if (currentSize === 0) {
            fileBrowserPanelRef.current.resize(12);
          }
        }
      } else {
        // Ensure it's collapsed
        fileBrowserPanelRef.current.resize(0);
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [currentDocument?.id, showFileBrowser]);

  // Auto-save is always enabled - no need to disable it

  // Track last known server version timestamp to detect updates
  const lastServerUpdateRef = React.useRef<string | null>(null);
  const isPollingRef = React.useRef(false);

  // WebSocket collaboration hook for real-time editing
  // Must be declared before polling useEffect so isWsConnected is available
  const {
    isConnected: isWsConnected,
    isConnecting: isWsConnecting,
    activeUsers: wsActiveUsers,
    sendChange: wsSendChange,
    sendCursorUpdate: wsSendCursorUpdate, // Will be used for cursor tracking in future
  } = useCollaboration({
    documentId: documentId || null,
    enabled: !!documentId && canEdit, // Only enable if user can edit
    onContentChange: (newContent: string) => {
      // Handle content change from WebSocket (another user's edit)
      logDebug("[EditorPage] WebSocket content change received:", {
        contentLength: newContent.length,
        currentLength: content.length,
      });

      // Only update if content is different (avoid unnecessary updates)
      if (newContent !== content) {
        markPdfNeedsRecompile("remote-content-update");

        setContent(newContent);

        // Update current document
        if (currentDocument) {
          setCurrentDocument({
            ...currentDocument,
            content: newContent,
            word_count: newContent.split(/\s+/).filter((w) => w.length > 0)
              .length,
            character_count: newContent.length,
          });
          lastServerUpdateRef.current = currentDocument.updated_at;
        }

        // Mark as clean since this is from server
        markClean();
      }
    },
    onCursorUpdate: (userId: string, position: number, username: string) => {
      // Handle cursor update from other users
      // Update remote cursors state to display in editor
      setRemoteCursors((prev) => {
        // Find user info from activeUsers to get full_name
        const userInfo = wsActiveUsers.find(
          (u: any) => (u.id || u.user_id) === userId
        );
        const fullName = userInfo?.full_name;

        // Update or add cursor for this user
        const existingIndex = prev.findIndex((c) => c.userId === userId);
        const newCursor = {
          userId,
          username,
          full_name: fullName,
          position,
          color: getUserColor(userId),
        };

        if (existingIndex >= 0) {
          // Update existing cursor
          const updated = [...prev];
          updated[existingIndex] = newCursor;
          return updated;
        } else {
          // Add new cursor
          return [...prev, newCursor];
        }
      });
    },
    onPresenceUpdate: (users: Array<any>) => {
      // Handle presence update (who's online)
      logDebug("[EditorPage] Active users:", users.length);

      // Remove cursors for users who are no longer active
      setRemoteCursors((prev) => {
        const activeUserIds = new Set(
          users.map((u: any) => u.id || u.user_id).filter(Boolean)
        );
        return prev.filter((cursor) => activeUserIds.has(cursor.userId));
      });
    },
    onError: (error: string) => {
      logError("[EditorPage] WebSocket error:", error);
      // Don't show toast for WebSocket errors to avoid spam
      // User can still use polling as fallback
    },
  });

  // Listen for document deletion events (e.g., document deleted from another device)
  useEffect(() => {
    const handleDocumentDeleted = (event: CustomEvent) => {
      const { documentId: deletedDocId } = event.detail
      // Only navigate if the deleted document is the one currently open
      if (deletedDocId === documentId) {
        logDebug('[EditorPage] Document was deleted, navigating to documents page')
        // Navigate to documents page after a short delay to let the toast show
        setTimeout(() => {
          navigate('/documents')
        }, 2000)
      }
    }

    window.addEventListener('document-deleted', handleDocumentDeleted as EventListener)
    return () => {
      window.removeEventListener('document-deleted', handleDocumentDeleted as EventListener)
    }
  }, [documentId, navigate])

  // Check for pending shares when document loads (for non-authenticated users or email mismatch)
  useEffect(() => {
    const checkPendingShare = async () => {
      if (!documentId || !isAuthenticated) {
        // Check if there's an email in URL params (from shared link)
        const emailParam = searchParams.get('email')
        if (emailParam && documentId) {
          try {
            const shareInfo = await documentSharesAPI.checkSharedByEmail(documentId, emailParam)
            if (shareInfo.is_shared && shareInfo.requires_registration) {
              setPendingShare({ email: shareInfo.email || emailParam })
              return
            }
          } catch (error) {
            logDebug('Error checking pending share:', error)
          }
        }
        return
      }

      // If user is authenticated, check if their email matches a pending share
      if (user?.email && documentId) {
        try {
          const shareInfo = await documentSharesAPI.checkSharedByEmail(documentId, user.email)
          if (shareInfo.is_shared && shareInfo.requires_registration) {
            // User is logged in but share is still pending - this shouldn't happen
            // but if it does, the backend will auto-activate it on next permission check
            logDebug('Pending share found for authenticated user - will be auto-activated')
          }
        } catch (error) {
          logDebug('Error checking pending share for authenticated user:', error)
        }
      }
    }

    checkPendingShare()
  }, [documentId, isAuthenticated, user?.email, searchParams])

  // Load document if documentId is provided
  useEffect(() => {
    const loadDocument = async () => {
      if (
        documentId &&
        (!currentDocument || currentDocument.id !== documentId)
      ) {
        setIsLoadingDocument(true);

        try {
          const document = await documentsAPI.getById(documentId);
          logDebug("[EditorPage] Loading document:", {
            id: document.id,
            title: document.title,
            contentLength: document.content.length,
            updatedAt: document.updated_at,
          });

          // Clear any previous compilation errors/warnings when loading a new document
          setCompilationErrors([]);
          setCompilationWarnings([]);
          setHasFatalErrors(false);
          setShowDebugPanel(false);

          setCurrentDocument(document);
          setContent(document.content);
          markClean(); // Mark as clean since we just loaded it
          lastServerUpdateRef.current = document.updated_at; // Track server version

          // For LaTeX documents, load main .tex file (main.tex if exists, otherwise first .tex file)
          if (document.document_type === "latex") {
            const isImported = searchParams.get("imported") === "true";

            if (isImported) {
              setIsWaitingForZipImport(true);
              logDebug(
                "[EditorPage] ZIP import detected, waiting for files to be ready..."
              );
            }

            // For imported ZIPs, wait longer (up to 30 seconds) to allow backend processing
            // For regular documents, only retry once
            const maxRetries = isImported ? 60 : 1; // 60 retries * 500ms = 30 seconds
            const retryDelay = 500; // 500ms between retries
            let retries = maxRetries;
            let mainTex = null;
            let fileTree = null;
            let lastFileCount = 0;

            while (retries > 0 && !mainTex) {
              try {
                fileTree = await latexAPI.getFileTree(documentId);
                const currentFileCount = fileTree.files?.length || 0;

                // For imported ZIPs, ensure files are actually ready
                if (isImported) {
                  // If file count changed, backend is still processing - wait more
                  if (currentFileCount !== lastFileCount && retries > 10) {
                    logDebug(
                      `[EditorPage] File count changed (${lastFileCount} -> ${currentFileCount}), waiting for processing...`
                    );
                    lastFileCount = currentFileCount;
                    await new Promise((resolve) => setTimeout(resolve, retryDelay));
                    retries--;
                    continue;
                  }
                  lastFileCount = currentFileCount;

                  // If no files yet, wait for backend to process ZIP
                  if (currentFileCount === 0 && retries > 10) {
                    logDebug(
                      `[EditorPage] No files yet, waiting for ZIP processing... (${retries - 1
                      } retries left)`
                    );
                    await new Promise((resolve) => setTimeout(resolve, retryDelay));
                    retries--;
                    continue;
                  }

                  // If we have files, check if they're from template or actual upload
                  // Skip template files - we want uploaded files only
                  const nonTemplateFiles = fileTree.files.filter(
                    (f: DocumentFile) =>
                      !f.file_path.includes("template") &&
                      f.file_path !== "bibliography.bib" // Skip default bibliography
                  );

                  // If we only have template files, wait for actual upload to complete
                  if (nonTemplateFiles.length === 0 && retries > 10) {
                    logDebug(
                      `[EditorPage] Only template files found, waiting for ZIP upload... (${retries - 1
                      } retries left)`
                    );
                    await new Promise((resolve) => setTimeout(resolve, retryDelay));
                    retries--;
                    continue;
                  }
                }

                // First try to find file with is_main_tex flag (excluding template files)
                mainTex = fileTree.files.find(
                  (f: DocumentFile) =>
                    f.is_main_tex &&
                    !f.file_path.includes("template")
                );

                // If no main_tex found, find first .tex file at root level (excluding templates)
                if (!mainTex) {
                  const texFiles = fileTree.files.filter(
                    (f: DocumentFile) =>
                      f.file_type === "file" &&
                      f.file_path.endsWith(".tex") &&
                      !f.file_path.includes("/") && // Root level only
                      !f.file_path.includes("template") // Exclude template files
                  );
                  if (texFiles.length > 0) {
                    // Sort by name and take first
                    texFiles.sort((a, b) => a.name.localeCompare(b.name));
                    mainTex = texFiles[0];
                    logDebug(
                      `[EditorPage] No main.tex found, using first .tex file: ${mainTex.name}`
                    );
                  }
                }

                if (mainTex) {
                  // Load content first
                  const blob = await latexAPI.getFileContent(
                    documentId,
                    mainTex.id
                  );
                  const text = await blob.text();

                  // For imported ZIPs, make sure we're not loading empty template content
                  if (isImported) {
                    // Check if content looks like a template (very short or contains template markers)
                    const isTemplateContent =
                      text.trim().length < 100 ||
                      text.includes("Your content here") ||
                      text.includes("Your Title Here") ||
                      text.includes("Your Name");

                    if (isTemplateContent && retries > 10) {
                      logDebug(
                        `[EditorPage] Content appears to be template, waiting for ZIP processing... (${retries - 1
                        } retries left)`
                      );
                      await new Promise((resolve) => setTimeout(resolve, retryDelay));
                      retries--;
                      mainTex = null; // Reset to continue waiting
                      continue;
                    }
                  }

                  // CRITICAL: Always create/open tab with the loaded content
                  // This ensures getActiveTab() never returns null when a file is open
                  await addOrSwitchToTab(mainTex, text);
                  
                  setIsWaitingForZipImport(false);
                  logDebug("[EditorPage] Successfully loaded main file in tab:", mainTex.name);
                  break;
                } else if (retries > 1) {
                  // Wait a bit before retrying
                  logDebug(
                    `[EditorPage] Main file not found, retrying in ${retryDelay}ms... (${retries - 1
                    } retries left)`
                  );
                  await new Promise((resolve) => setTimeout(resolve, retryDelay));
                }
              } catch (error) {
                logError("Failed to load main .tex file:", error);
                if (retries > 1) {
                  await new Promise((resolve) => setTimeout(resolve, retryDelay));
                }
              }
              retries--;
            }

            if (!mainTex && isImported) {
              logWarn(
                "[EditorPage] Main file not found after import, files may still be processing"
              );
              setIsWaitingForZipImport(false); // Stop showing spinner even if files aren't ready
            }

            // Clean up query parameter after loading
            if (isImported) {
              setSearchParams({}, { replace: true });
            }

            // Trigger initial project scan for citation indexing (silently, in background)
            try {
              const metadata = await latexAPI.getProjectMetadata(documentId);
              const scannedAt = metadata?.scanned_at;
              const oneHourAgo = new Date(
                Date.now() - 60 * 60 * 1000
              ).toISOString();

              // Only scan if never scanned or scan is older than 1 hour
              if (!scannedAt || scannedAt < oneHourAgo) {
                logDebug(
                  "[EditorPage] Triggering initial project scan for citation indexing"
                );
                latexAPI.rescanProject(documentId).catch((error) => {
                  logWarn(
                    "[EditorPage] Failed to scan project on load:",
                    error
                  );
                  // Don't show error to user - this is background operation
                });
              } else {
                logDebug(
                  "[EditorPage] Project recently scanned, skipping initial scan",
                  { scannedAt }
                );
              }
            } catch (error) {
              logWarn("[EditorPage] Failed to check project metadata:", error);
              // Try to scan anyway
              latexAPI.rescanProject(documentId).catch((scanError) => {
                logWarn("[EditorPage] Failed to scan project:", scanError);
              });
            }
          }

          // Mark as opened for recent tracking
          await documentsAPI.markOpened(documentId);
        } catch (error) {
          logError("Failed to load document:", error);
        } finally {
          setIsLoadingDocument(false);
        }
      }
    };

    loadDocument();
  }, [documentId, currentDocument, setCurrentDocument, setContent, markClean]);

  // Poll for document updates (ONLY when WebSocket is disconnected)
  // WebSocket handles real-time updates when connected, so polling is only needed as fallback
  useEffect(() => {
    if (!documentId || !currentDocument || currentDocument.id !== documentId) {
      return;
    }

    // Only enable polling if WebSocket is NOT connected
    // WebSocket provides real-time updates, so polling is unnecessary when connected
    if (isWsConnected) {
      // Don't even create the interval if WebSocket is connected
      return;
    }

    logDebug("[EditorPage] WebSocket disconnected - enabling polling fallback");

    // Poll less frequently when WebSocket is down (every 15 seconds instead of 5)
    const pollInterval = setInterval(async () => {
      // Skip polling if we're already polling, user has unsaved changes, or WebSocket reconnected
      if (isPollingRef.current || isDirty || isSaving || isWsConnected) {
        return;
      }

      try {
        isPollingRef.current = true;

        // Fetch latest document from server
        const latestDocument = await documentsAPI.getById(documentId);

        // Compare timestamps to see if server version is newer
        const serverUpdatedAt = new Date(latestDocument.updated_at).getTime();
        const localUpdatedAt = lastServerUpdateRef.current
          ? new Date(lastServerUpdateRef.current).getTime()
          : 0;

        // Only update if server version is newer
        if (serverUpdatedAt > localUpdatedAt) {
          // Check if content is actually different
          if (latestDocument.content !== content) {
            logDebug(
              "[EditorPage] Detected update from another user, refreshing document...",
              {
                serverTime: latestDocument.updated_at,
                localTime: lastServerUpdateRef.current,
                contentDiff: latestDocument.content.length !== content.length,
              }
            );

            // Update document and content
            setCurrentDocument(latestDocument);
            setContent(latestDocument.content);
            lastServerUpdateRef.current = latestDocument.updated_at;
            markClean();

          } else {
            // Server version is newer but content is same (metadata update like title, etc.)
            logDebug(
              "[EditorPage] Server version newer but content unchanged, updating metadata..."
            );
            setCurrentDocument(latestDocument);
            lastServerUpdateRef.current = latestDocument.updated_at;
          }
        }
      } catch (error) {
        logError("[EditorPage] Failed to poll for document updates:", error);
        // Don't show error toast for polling failures to avoid spam
      } finally {
        isPollingRef.current = false;
      }
    }, 15000); // Poll every 15 seconds (reduced from 5, only when WebSocket is down)

    return () => clearInterval(pollInterval);
  }, [
    documentId,
    currentDocument,
    content,
    isDirty,
    isSaving,
    isWsConnected,
    setCurrentDocument,
    setContent,
    markClean,
  ]);

  // Update lastServerUpdateRef when document is saved successfully
  // This ensures we track the latest server version after our own saves
  useEffect(() => {
    if (
      currentDocument &&
      !isSaving &&
      !isDirty &&
      currentDocument.updated_at
    ) {
      lastServerUpdateRef.current = currentDocument.updated_at;
    }
  }, [currentDocument, isSaving, isDirty]);

  // Mark document as clean after successful save
  useEffect(() => {
    // When save completes successfully and we're not currently saving,
    // mark the editor as clean ONLY if content matches saved document AND we're not dirty
    // This prevents marking clean while user is still typing
    if (
      !isSaving &&
      currentDocument &&
      content === currentDocument.content &&
      isDirty
    ) {
      // Double-check: only mark clean if content truly matches (not just after auto-save)
      // Wait a bit to ensure user isn't still typing
      const timeoutId = setTimeout(() => {
        if (content === currentDocument.content && !isSaving) {
          markClean();
        }
      }, 500); // Small delay to ensure user finished typing

      return () => clearTimeout(timeoutId);
    }
  }, [isSaving, currentDocument, content, isDirty, markClean]);

  // Initialize with empty content if no document
  useEffect(() => {
    if (!currentDocument && content === "") {
      // Don't set any default content - let user start fresh
    }
  }, [currentDocument, content, setContent]);

  const handleContentChange = (newContent: string) => {
    // Prevent editing if user doesn't have edit permission
    if (!canEdit) {
      return;
    }

    const activeTab = getActiveTab();
    // Only mark PDF as stale if we're editing a .tex file
    const currentFilePath =
      selectedFilePathRef.current || activeTab?.file.file_path;
    const isTexFile = !currentFilePath || currentFilePath.endsWith(".tex");
    const activeFile = activeTab?.file;
    
    // CRITICAL FIX: Always save directly to the active file if one is open
    // This prevents content from being saved to the wrong file when get_main_tex_file()
    // returns a different file (e.g., if main.tex isn't marked as is_main_tex=True)
    // Only use triggerAutosave (document update path) when there's no active file
    const shouldSaveToFile = activeFile && currentDocument?.document_type === "latex";
    const isMainDocumentContext =
      !activeFile ||
      currentDocument?.document_type !== "latex";

    if (isTexFile) {
      markPdfNeedsRecompile("local-edit");
      // Update the last .tex content hash when content changes
      // This ensures we can detect if content changed when switching files
      const newHash = computeContentHash(newContent);
      lastTexContentHashRef.current = newHash;
    }

    const oldContent = content;
    
    // Don't update if we're switching tabs and this is the expected content change
    // (the editor might fire onChange when we programmatically set content)
    if (isSwitchingTabsRef.current) {
      if (expectedContentOnSwitchRef.current !== null && newContent === expectedContentOnSwitchRef.current) {
        // This is the expected content change from switching tabs, ignore it
        return;
      }
      // If it's a different content change during switch, it's a real user edit - allow it
    }
    
    setContent(newContent);

    // Update active tab's content
    updateActiveTabContent(newContent);

    if (currentDocument) {
      if (shouldSaveToFile) {
        // For LaTeX documents with an active file, always save directly to that file
        // This ensures main.tex saves to main.tex, major.tex saves to major.tex, etc.
        scheduleFileSave(activeFile, newContent);
      } else if (isMainDocumentContext) {
        // Only use document update path when there's no active file
        // (non-LaTeX documents or when no file is selected)
        setCurrentDocument({
          ...currentDocument,
          content: newContent,
          word_count: newContent.split(/\s+/).filter((w) => w.length > 0)
            .length,
          character_count: newContent.length,
        });

        // Send change via WebSocket if connected (real-time collaboration)
        if (isWsConnected && newContent !== oldContent) {
          // Calculate change for operational transform
          // Simple implementation: send full content for now
          // TODO: Implement proper operational transform (insert/delete operations)
          const change = calculateChange(oldContent, newContent);
          if (change) {
            wsSendChange(change, newContent);
          }
        }

        // Trigger debounced autosave (saves 2 seconds after user stops typing)
        // This ensures persistence even if WebSocket fails
        // Pass current content to autosave
        triggerAutosave(newContent);
      }
    }
  };

  // Calculate change between old and new content
  // Simple implementation - can be improved with proper OT
  const calculateChange = (
    oldContent: string,
    newContent: string
  ): {
    type: "insert" | "delete";
    position: number;
    length: number;
    content?: string;
  } | null => {
    if (oldContent === newContent) return null;

    // Find first difference
    let position = 0;
    while (
      position < Math.min(oldContent.length, newContent.length) &&
      oldContent[position] === newContent[position]
    ) {
      position++;
    }

    // Find last difference
    let oldEnd = oldContent.length;
    let newEnd = newContent.length;
    while (
      oldEnd > position &&
      newEnd > position &&
      oldContent[oldEnd - 1] === newContent[newEnd - 1]
    ) {
      oldEnd--;
      newEnd--;
    }

    const deletedLength = oldEnd - position;
    const insertedLength = newEnd - position;

    if (deletedLength > 0 && insertedLength === 0) {
      // Deletion
      return {
        type: "delete",
        position,
        length: deletedLength,
      };
    } else if (insertedLength > 0) {
      // Insertion (or replacement)
      return {
        type: "insert",
        position,
        length: insertedLength,
        content: newContent.substring(position, newEnd),
      };
    }

    return null;
  };

  // Unified citation insertion handler
  const handleCitationInsert = useCallback(
    async (citation: string, paper?: CitationPaper) => {
      if (!currentDocument) {
        return;
      }

      // Get cursor position from editor store (fallback to end of document)
      const insertPos = cursorPosition ?? content.length;

      // Generate citation text
      let citationText = "";
      let bibtexKey: string | undefined;

      if (mode === "latex" && paper) {
        // For LaTeX, use bibtexKey from paper or generate one
        bibtexKey = paper.bibtexKey;
        if (!bibtexKey && paper.authors && paper.authors.length > 0) {
          // Generate key from paper metadata
          const firstAuthor =
            paper.authors[0].split(/\s+/).pop()?.toLowerCase() || "unknown";
          const year = paper.year ? String(paper.year) : "noyear";
          const titleWords = paper.title?.split(/\s+/).slice(0, 2) || [];
          const titlePart = titleWords
            .map((w: string) =>
              w
                .toLowerCase()
                .replace(/[^a-z0-9]/g, "")
                .substring(0, 4)
            )
            .join("");
          bibtexKey = `${firstAuthor}${year}${titlePart}`.substring(0, 30);
        }
        citationText = bibtexKey ? `\\cite{${bibtexKey}}` : "";
      } else if (citation) {
        // For non-LaTeX, use provided citation text
        citationText = citation;
      } else {
        return;
      }

      if (!citationText) {
        return;
      }

      // Insert citation at cursor position
      const before = content.slice(0, insertPos);
      const after = content.slice(insertPos);
      // Add space before citation if needed
      const needsSpaceBefore =
        insertPos > 0 && !before.match(/\s$/) && !citationText.startsWith(" ");
      const needsSpaceAfter =
        after.length > 0 && !after.match(/^\s/) && !citationText.endsWith(" ");
      const newContent =
        before +
        (needsSpaceBefore ? " " : "") +
        citationText +
        (needsSpaceAfter ? " " : "") +
        after;

      handleContentChange(newContent);

      // For LaTeX documents, add BibTeX entry to .bib file or inline bibliography
      if (mode === "latex" && paper && bibtexKey) {
        const bibInfo = detectBibliographyType(newContent);
        try {
          const bibtexEntry = generateBibtexEntry(paper, bibtexKey);
          const bibFile = await latexAPI.addBibtexEntry(
            currentDocument.id,
            bibtexEntry
          );
          // Clear citation cache so autocomplete shows new citation
          clearCitationCache(currentDocument.id);
          triggerFileBrowserRefresh();
          if (bibInfo.type === "internal") {
            const bibitemEntry = generateBibitemEntry(paper, bibtexKey);
            const updatedContent = insertBibitemIntoDocument(
              newContent,
              bibitemEntry,
              bibInfo
            );
            if (updatedContent !== newContent) {
              handleContentChange(updatedContent);
            }
          } else if (bibInfo.type === "external") {
          } else {
            let updatedContent: string | null = null;
            if (bibFile?.name) {
              updatedContent = insertBibliographyCommandsLocally(
                newContent,
                bibFile.name
              );
            }
            if (updatedContent && updatedContent !== newContent) {
              handleContentChange(updatedContent);
            }
          }
        } catch (error) {
          logError("Error adding BibTeX entry:", error);
        }
      } else {
      }
    },
    [
      currentDocument,
      mode,
      content,
      cursorPosition,
      handleContentChange,
      triggerFileBrowserRefresh,
      insertBibliographyCommandsLocally,
    ]
  );

  // Manual save removed - auto-save handles all saving automatically

  const handleTitleEdit = () => {
    // Prevent editing title if user doesn't have edit permission
    if (!canEdit) {
      return;
    }

    if (currentDocument) {
      setTitleValue(currentDocument.title);
      setIsEditingTitle(true);
    }
  };

  const handleTitleSave = async () => {
    if (!currentDocument || !titleValue.trim()) {
      setIsEditingTitle(false);
      return;
    }

    // Don't save if title hasn't changed
    if (titleValue.trim() === currentDocument.title) {
      setIsEditingTitle(false);
      return;
    }

    try {
      setIsSavingTitle(true);
      // Update document title using the updateDocument method
      await updateDocument(currentDocument.id, {
        title: titleValue.trim(),
        content: currentDocument.content,
      });
      setIsEditingTitle(false);
    } catch (error) {
      logError("Failed to update title:", error);
      setIsEditingTitle(false);
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleTitleSave();
    } else if (e.key === "Escape") {
      setIsEditingTitle(false);
    }
  };

  // Handle LaTeX compilation
  const handleCompile = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      if (
        !currentDocument ||
        currentDocument.document_type !== "latex" ||
        !content.trim()
      ) {
        return;
      }

      // Ensure any pending file edits are flushed before compilation
      await flushPendingFileSave({ silent: true });

      // Don't compile if a non-.tex file is selected - use ref to avoid stale state
      const activeTab = getActiveTab();
      const currentFilePath =
        selectedFilePathRef.current || activeTab?.file.file_path;
      const isTexFile = !currentFilePath || currentFilePath.endsWith(".tex");

      // Check content - must have \documentclass to be valid LaTeX
      const hasDocumentClass = content.includes("\\documentclass");
      const looksLikeBibtex =
        content.trim().startsWith("@") && !hasDocumentClass;

      // Don't compile if:
      // 1. Not a .tex file, OR
      // 2. Content looks like BibTeX (starts with @ and no \documentclass), OR
      // 3. Content doesn't have \documentclass (not valid LaTeX)
      if (!isTexFile || looksLikeBibtex || !hasDocumentClass) {
        logDebug(
          "[EditorPage] handleCompile: Skipping compilation - not a valid .tex file",
          {
            currentFilePath,
            isTexFile,
            looksLikeBibtex,
            hasDocumentClass,
            contentStart: content.substring(0, 100),
          }
        );
        return;
      }

      setIsCompiling(true);
      try {
        // Determine which file to compile
        // CRITICAL: Always try to determine the file path to compile
        // This ensures main.tex and other files compile correctly
        let mainFilePath: string | undefined = undefined;
        const activeTab = getActiveTab();
        
        if (activeTab && activeTab.file.file_path.endsWith(".tex")) {
          mainFilePath = activeTab.file.file_path;
          logDebug(
            "[EditorPage] handleCompile: Compiling specific .tex file from active tab",
            { filePath: mainFilePath, fileName: activeTab.file.name }
          );
        } else {
          // Fallback: Try to find the file from selectedFilePathRef or content
          const currentFilePath = selectedFilePathRef.current;
          if (currentFilePath && currentFilePath.endsWith(".tex")) {
            mainFilePath = currentFilePath;
            logDebug(
              "[EditorPage] handleCompile: Using file path from ref",
              { filePath: mainFilePath }
            );
          } else {
            logDebug(
              "[EditorPage] handleCompile: No .tex file path determined, backend will use default"
            );
          }
        }

        // Check if BibTeX is needed
        const hasBibliographyCommands =
          /\\(?:bibliography|addbibresource|printbibliography|cite)/i.test(
            content
          );

        // Check for .bib files in project
        let hasBibFiles = false;
        try {
          const fileTree = await latexAPI.getFileTree(currentDocument.id);
          const checkForBibFiles = (files: any[]): boolean => {
            for (const file of files || []) {
              if (file.name.endsWith(".bib")) {
                return true;
              }
              if (file.children && file.children.length > 0) {
                if (checkForBibFiles(file.children)) {
                  return true;
                }
              }
            }
            return false;
          };
          hasBibFiles = checkForBibFiles(fileTree.files || []);
        } catch (error) {
          logWarn("[EditorPage] Could not check for .bib files:", error);
        }

        const shouldIncludeBibtex = hasBibliographyCommands || hasBibFiles;

        // Use user's preferred compiler or default to pdflatex
        const preferredCompiler = (aiPreferences?.latex_compiler as 'pdflatex' | 'xelatex' | 'lualatex' | undefined) || "pdflatex";

        // Compute content hash for cache lookup
        const contentHash = computeContentHash(content);

        // Check cache first if we have a file path AND this is an auto-compile (silent)
        // For explicit user actions (non-silent), skip cache to ensure changes are compiled
        let compileResult: any = null;
        // First, check in-memory blob: if it was compiled for this file + hash, reuse it
        if (
          !silent &&
          compiledPdfBlobRef.current &&
          compiledPdfFilePathRef.current === (mainFilePath || null) &&
          compiledContentHashRef.current === contentHash
        ) {
          logDebug(
            "[EditorPage] Reusing in-memory compiled PDF for current document and main_file_path",
            {
              documentId: currentDocument.id,
              mainFilePath,
              contentHash: contentHash.substring(0, 16) + "...",
            }
          );
          // We already have the correct PDF in memory; just mark it as fresh
          setIsPdfStale(false);
          setIsCompiling(false);
          return;
        }
        if (mainFilePath && silent) {
          logDebug("[EditorPage] Checking PDF cache before compilation (auto-compile)", {
            documentId: currentDocument.id,
            mainFilePath,
            contentHash: contentHash.substring(0, 16) + "..."
          });

          const cachedResult = await latexAPI.getCachedPdf(
            currentDocument.id,
            mainFilePath,
            contentHash
          );

          if (cachedResult && cachedResult.pdf_data) {
            logDebug("[EditorPage] PDF found in cache, using cached version");
            compileResult = cachedResult;
          }
        } else if (!silent) {
          logDebug("[EditorPage] Skipping cache check for explicit compilation", {
            mainFilePath,
            contentHash: contentHash.substring(0, 16) + "..."
          });
        }

        // If not cached, compile
        if (!compileResult) {
          logDebug("[EditorPage] PDF not in cache, compiling...", {
            mainFilePath: mainFilePath || "undefined (will use backend default)",
            hasActiveTab: !!getActiveTab(),
            contentHash: contentHash.substring(0, 16) + "...",
            silent: silent
          });
          
          // CRITICAL: Always send main_file_path, even if undefined
          // The backend will use get_main_tex_file() as fallback, but we prefer explicit paths
          const compileRequest: any = {
            compiler: preferredCompiler,
            runs: 2,
            include_bibtex: shouldIncludeBibtex,
            content_hash: contentHash,
            // Force recompile if this is an explicit user action (not silent auto-compile)
            // This ensures changes are always compiled, even if cache matches incorrectly
            force_recompile: !silent,
          };
          
          // Only include main_file_path if we have it (don't send null/undefined)
          if (mainFilePath) {
            compileRequest.main_file_path = mainFilePath;
          }
          
          compileResult = await latexAPI.compileProject(
            currentDocument.id,
            compileRequest
          );
        }

        // Safeguard: ensure we have a valid result object
        if (!compileResult) {
          const fallbackError =
            "Compilation failed: no response from server. Please try again.";
          setCompilationErrors([fallbackError]);
          setCompilationWarnings([]);
          setHasFatalErrors(true);
          setCompiledPdfBlob(null);
          compiledPdfBlobRef.current = null;
          setIsPdfStale(true);
          setShowDebugPanel(true);
          return;
        }

        // Normalize errors/warnings to arrays and update state (deduplicated)
        const resultErrors = Array.isArray(compileResult.errors)
          ? compileResult.errors
          : [];
        const resultWarnings = Array.isArray(compileResult.warnings)
          ? compileResult.warnings
          : [];

        setCompilationErrors(deduplicateStrings(resultErrors));
        setCompilationWarnings(deduplicateStrings(resultWarnings));
        setHasFatalErrors(compileResult.has_fatal_errors || false);

        // Convert base64 PDF data to Blob if available
        let pdfBlob: Blob | null = null;
        if (compileResult.pdf_data) {
          try {
            // Convert base64 to binary
            const binaryString = atob(compileResult.pdf_data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            pdfBlob = new Blob([bytes], { type: "application/pdf" });
            // Track which file this PDF was compiled for
            compiledPdfFilePathRef.current = mainFilePath || null;
          } catch (error) {
            logError(
              "[EditorPage] Failed to convert base64 PDF data to Blob:",
              error
            );
          }
        }

        // Handle errors - show debug panel, but still show PDF if it was generated (like Overleaf)
        if (resultErrors.length > 0) {
          setShowDebugPanel(true); // Auto-open debug panel for errors
          // NO TOAST - errors are shown in debug panel only
          // If PDF was generated despite errors, show it (like Overleaf behavior)
          if (pdfBlob) {
            // PDF exists despite errors - show it with errors in debug panel (like Overleaf)
            const newBlob = new Blob([pdfBlob], { type: "application/pdf" });
            setCompiledPdfBlob(newBlob);
            compiledPdfBlobRef.current = newBlob;
            setIsPdfStale(false);
            // Continue to store PDF and update state
          } else {
            // No PDF generated - fatal error
            setCompiledPdfBlob(null);
            compiledPdfBlobRef.current = null;
            return;
          }
        }

        // Handle warnings - show PDF, but allow user to view warnings in debug panel
        // Compilation complete - errors shown in debug panel only

        // Store the compiled PDF blob so DocumentPreview can use it
        if (pdfBlob) {
          const newBlob = new Blob([pdfBlob], { type: "application/pdf" });
          setCompiledPdfBlob(newBlob);
          compiledPdfBlobRef.current = newBlob;
          compiledPdfFilePathRef.current = mainFilePath || null;
          logDebug("[EditorPage] Set compiled PDF blob after compilation", {
            blobSize: newBlob.size,
            blobType: newBlob.type,
            compiledFor: mainFilePath || 'undefined'
          });
          const contentHash = computeContentHash(content);
          compiledContentHashRef.current = contentHash;
          lastTexContentHashRef.current = contentHash; // Update last viewed .tex content hash
          setIsPdfStale(false);

          // Persist PDF blob to localStorage so it survives navigation
          // Only store if PDF is small enough (localStorage has ~5-10MB limit)
          const MAX_PDF_SIZE_FOR_STORAGE = 4 * 1024 * 1024; // 4MB (safe limit)

          if (currentDocument?.id && pdfBlob.size <= MAX_PDF_SIZE_FOR_STORAGE) {
            try {
              // Convert blob to base64 for localStorage
              const reader = new FileReader();
              reader.onloadend = () => {
                const base64data = reader.result as string;
                if (base64data) {
                  try {
                    const safeMainPath = (mainFilePath || "default").replace(
                      /[^a-zA-Z0-9_\-./]/g,
                      "_"
                    );
                    const baseKey = `pdf_blob_${currentDocument.id}_${safeMainPath}`;
                    // Store blob, timestamp, and hash scoped to document + main_file_path
                    localStorage.setItem(baseKey, base64data);
                    localStorage.setItem(
                      `${baseKey}_timestamp`,
                      Date.now().toString()
                    );
                    localStorage.setItem(
                      `${baseKey}_hash`,
                      contentHash
                    );
                    logDebug(
                      "[EditorPage] Persisted PDF blob to localStorage",
                      {
                        size: pdfBlob.size,
                        base64Size: base64data.length,
                      }
                    );
                  } catch (storageError: any) {
                    // Handle quota exceeded or other storage errors
                    if (storageError.name === "QuotaExceededError") {
                      logWarn(
                        "[EditorPage] localStorage quota exceeded, PDF not persisted. PDF will need to be recompiled after navigation."
                      );
                      // Try to clear old PDFs to make space
                      try {
                        // Clear PDFs older than 1 hour
                        const oneHourAgo = Date.now() - 60 * 60 * 1000;
                        const keysToDelete: string[] = [];
                        for (let i = 0; i < localStorage.length; i++) {
                          const key = localStorage.key(i);
                          if (key && key.startsWith("pdf_blob_") && key.endsWith("_timestamp")) {
                            const timestamp = parseInt(
                              localStorage.getItem(key) || "0"
                            );
                            if (timestamp < oneHourAgo) {
                              // Derive base key without suffix
                              const baseKey = key.replace(/_timestamp$/, "");
                              keysToDelete.push(baseKey);
                            }
                          }
                        }
                        for (const baseKey of keysToDelete) {
                          localStorage.removeItem(baseKey);
                          localStorage.removeItem(`${baseKey}_timestamp`);
                          localStorage.removeItem(`${baseKey}_hash`);
                        }
                        // Try again after cleanup
                        try {
                          const safeMainPathRetry = (mainFilePath || "default").replace(
                            /[^a-zA-Z0-9_\-./]/g,
                            "_"
                          );
                          const retryBaseKey = `pdf_blob_${currentDocument.id}_${safeMainPathRetry}`;
                          localStorage.setItem(retryBaseKey, base64data);
                          localStorage.setItem(
                            `${retryBaseKey}_timestamp`,
                            Date.now().toString()
                          );
                          localStorage.setItem(
                            `${retryBaseKey}_hash`,
                            contentHash
                          );
                          logDebug(
                            "[EditorPage] Persisted PDF blob after cleanup"
                          );
                        } catch (retryError) {
                          logWarn(
                            "[EditorPage] Still unable to persist PDF after cleanup"
                          );
                        }
                      } catch (cleanupError) {
                        logError(
                          "[EditorPage] Failed to cleanup old PDFs:",
                          cleanupError
                        );
                      }
                    } else {
                      logError(
                        "[EditorPage] Failed to persist PDF blob:",
                        storageError
                      );
                    }
                  }
                }
              };
              reader.onerror = () => {
                logError("Failed to read PDF blob for persistence");
              };
              reader.readAsDataURL(pdfBlob);
            } catch (error) {
              logError("Failed to persist PDF blob:", error);
            }
          } else if (
            currentDocument?.id &&
            pdfBlob.size > MAX_PDF_SIZE_FOR_STORAGE
          ) {
            logWarn("[EditorPage] PDF too large for localStorage persistence", {
              size: pdfBlob.size,
              maxSize: MAX_PDF_SIZE_FOR_STORAGE,
            });
          }
        }
      } catch (error: any) {
        logError("Compilation error:", error);

        // Handle LatexCompilationError
        // NO TOAST NOTIFICATIONS - all errors shown in debug panel only
        if (error instanceof LatexCompilationError) {
          setCompilationErrors(deduplicateStrings(error.errors || []));
          setCompilationWarnings(deduplicateStrings(error.warnings || []));
          setHasFatalErrors(true);
          setShowDebugPanel(true); // Auto-open debug panel for errors
          setCompiledPdfBlob(null);
          compiledPdfBlobRef.current = null;
          setIsCompiling(false);
          // NO TOAST - errors shown in debug panel
        } else if (error.response?.data) {
          // Handle API error response (should be LatexCompileResponse now)
          const errorData = error.response.data;
          if (errorData.errors || errorData.warnings) {
            setCompilationErrors(deduplicateStrings(errorData.errors || []));
            setCompilationWarnings(deduplicateStrings(errorData.warnings || []));
            setHasFatalErrors(errorData.has_fatal_errors || true);
            setShowDebugPanel(true);

            // Check if PDF was generated despite errors (like Overleaf)
            if (errorData.pdf_data) {
              try {
                const binaryString = atob(errorData.pdf_data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }
                const pdfBlob = new Blob([bytes], { type: "application/pdf" });
                setCompiledPdfBlob(pdfBlob);
                compiledPdfBlobRef.current = pdfBlob;
                // Note: For error responses, we don't know which file was compiled, so leave compiledPdfFilePathRef as is
                // Note: compiledPdfFilePathRef is set in handleCompile when PDF is compiled
                // For restored PDFs, we don't know which file it was for, so leave it null
              } catch (e) {
                logError(
                  "[EditorPage] Failed to convert error response PDF data:",
                  e
                );
                setCompiledPdfBlob(null);
                compiledPdfBlobRef.current = null;
                compiledPdfFilePathRef.current = null;
              }
            } else {
              setCompiledPdfBlob(null);
              compiledPdfBlobRef.current = null;
              compiledPdfFilePathRef.current = null;
            }
            setIsCompiling(false);
            // NO TOAST - errors shown in debug panel
          } else {
            // Generic API error - handle gracefully
            const errorMessage =
              errorData.detail ||
              errorData.message ||
              errorData ||
              "Compilation failed. Please try again.";
            setCompilationErrors([errorMessage]);
            setCompilationWarnings([]);
            setHasFatalErrors(true);
            setShowDebugPanel(true);
            setCompiledPdfBlob(null);
            compiledPdfBlobRef.current = null;
            setIsCompiling(false);
            // NO TOAST - errors shown in debug panel
          }
        } else {
          // Network error or other unexpected error - handle gracefully
          const errorMessage = error?.message || "";
          const isCompilationResultError =
            errorMessage.includes("Unable to retrieve compilation result") ||
            errorMessage.includes("Service error") ||
            errorMessage.includes("Compilation service error");

          if (isCompilationResultError) {
            // This is a transient service error - don't show as fatal
            // The compilation might have succeeded but result retrieval failed
            logWarn("[EditorPage] Compilation result retrieval failed, clearing stale errors");
            setCompilationErrors([]);
            setCompilationWarnings([]);
            setHasFatalErrors(false);
            setShowDebugPanel(false);
            // Don't clear PDF blob - it might still be valid from previous compilation
          } else {
            // Other errors - show them
            setCompilationErrors([errorMessage || "Network error. Please check your connection and try again."]);
            setCompilationWarnings([]);
            setHasFatalErrors(true);
            setShowDebugPanel(true);
            setCompiledPdfBlob(null);
            compiledPdfBlobRef.current = null;
            compiledPdfFilePathRef.current = null;
          }
          setIsCompiling(false);
          // NO TOAST - errors shown in debug panel
        }

        if (silent) {
          initialAutoCompileAttemptedRef.current = false;
        }
      } finally {
        setIsCompiling(false);
      }
    },
    [
      currentDocument,
      content,
      getActiveTab,
      flushPendingFileSave,
      aiPreferences?.latex_compiler,
    ]
  );

  useEffect(() => {
    if (!currentDocument || currentDocument.document_type !== "latex") return;

    // Use ref to avoid stale state issues
    const activeTab = getActiveTab();
    const currentFilePath =
      selectedFilePathRef.current || activeTab?.file.file_path;
    const isTexFile = !currentFilePath || currentFilePath.endsWith(".tex");

    // If viewing a non-.tex file, keep PDF visible but don't try to restore/compile
    // PDF will persist from when .tex file was last viewed
    // Don't change stale state - keep it as it was when we last viewed the .tex file
    if (!isTexFile) {
      logDebug("[EditorPage] Viewing non-.tex file, keeping PDF visible", {
        currentFilePath,
      });
      return;
    }

    // Check content - must have \documentclass to be valid LaTeX
    const hasDocumentClass = content.includes("\\documentclass");
    const looksLikeBibtex = content.trim().startsWith("@") && !hasDocumentClass;

    // If content doesn't look like valid LaTeX, don't try to restore PDF
    if (looksLikeBibtex || !hasDocumentClass) {
      logDebug(
        "[EditorPage] Content is not valid LaTeX, skipping PDF restoration",
        {
          currentFilePath,
          looksLikeBibtex,
          hasDocumentClass,
          contentStart: content.substring(0, 100),
        }
      );
      return;
    }

    if (!content.trim()) {
      // Empty content - clear PDF
      setCompiledPdfBlob(null);
      compiledPdfBlobRef.current = null;
      compiledContentHashRef.current = null;
      compiledPdfFilePathRef.current = null;
      setIsPdfStale(false);
      setIsPdfForDifferentFile(false);
      return;
    }

    const docId = currentDocument.id;
    const currentHash = computeContentHash(content);

    // Update the last .tex content hash we viewed
    lastTexContentHashRef.current = currentHash;

    // CRITICAL: Check if PDF was compiled for a different file.
    // If so, DO NOT clear it automatically – keep the preview, but mark it as
    // "for a different file" and stale so the user can decide to recompile.
    // Also check ref in case state hasn't updated yet
    const existingPdfBlob = compiledPdfBlob || compiledPdfBlobRef.current;
    const existingPdfFilePath = compiledPdfFilePathRef.current;
    
    if (existingPdfBlob && existingPdfFilePath && currentFilePath) {
      if (existingPdfFilePath !== currentFilePath) {
        logDebug(
          "[EditorPage] PDF was compiled for different file, marking as stale but keeping preview",
          {
            compiledFor: existingPdfFilePath,
            currentFile: currentFilePath,
          }
        );
        setIsPdfStale(true);
        setIsPdfForDifferentFile(true);
        // Do not auto-restore or auto-compile here; user can explicitly compile
        return;
      }
    }

    // If we already have a PDF with matching hash AND it's for the current file, mark as not stale
    if (
      compiledPdfBlob &&
      compiledContentHashRef.current === currentHash &&
      compiledPdfFilePathRef.current === currentFilePath
    ) {
      setIsPdfStale(false);
      setIsPdfForDifferentFile(false);
      return;
    }

    // If we have a PDF but hash doesn't match (or it's for a different file), mark as stale
    // This means the .tex content has changed since the PDF was compiled, or it's the wrong file
    if (
      compiledPdfBlob &&
      compiledContentHashRef.current &&
      compiledContentHashRef.current !== currentHash
    ) {
      setIsPdfStale(true);
      return;
    }

    // Try to restore cached PDF, or auto-compile if no PDF is available
    // Don't auto-compile if we're waiting for ZIP import
    if (isWaitingForZipImport) {
      logDebug(
        "[EditorPage] Skipping auto-compilation - waiting for ZIP import to complete"
      );
      return;
    }

    let cancelled = false;
    const ensurePdf = async () => {
      // Pass mainFilePath to restorePersistedPdfBlob so it uses the correct scoped key
      const restored = await restorePersistedPdfBlob(
        docId,
        currentDocument.updated_at,
        currentHash,
        currentFilePath || undefined
      );
      if (cancelled) return;
      if (restored) {
        setCompiledPdfBlob(restored);
        compiledPdfBlobRef.current = restored;
        compiledContentHashRef.current = currentHash;
        compiledPdfFilePathRef.current = currentFilePath || null;
        // Check if restored PDF matches current content hash
        if (compiledContentHashRef.current === currentHash) {
          setIsPdfStale(false);
        } else {
          // Content has changed, mark as stale
          setIsPdfStale(true);
        }
      } else {
        // No cached PDF available for this specific file
        // Check if we have ANY PDF blob (even if for a different file)
        const hasAnyPdf = compiledPdfBlob || compiledPdfBlobRef.current;
        // Re-compute currentFilePath here since it's in the async function scope
        const currentFilePathForCheck = selectedFilePathRef.current || getActiveTab()?.file.file_path;
        const pdfMatchesCurrentFile = compiledPdfFilePathRef.current === currentFilePathForCheck;
        
        // Only auto-compile if:
        // 1. No PDF available at all (not even for a different file), AND
        // 2. We haven't already attempted auto-compile for this file
        // If PDF exists but is for a different file, we already handled that above and returned early
        // So this path should only execute when there's truly no PDF at all
        
        if (!hasAnyPdf && !initialAutoCompileAttemptedRef.current) {
          initialAutoCompileAttemptedRef.current = true;
          logDebug(
            "[EditorPage] No PDF available at all, auto-compiling to show preview",
            {
              currentFilePath: currentFilePathForCheck,
            }
          );
          await handleCompile({ silent: true });
        } else if (hasAnyPdf && !pdfMatchesCurrentFile) {
          // PDF exists but is for a different file - this should have been caught above,
          // but if we get here, just mark as stale and don't auto-compile
          logDebug(
            "[EditorPage] PDF exists for different file, not auto-compiling",
            {
              currentFilePath: currentFilePathForCheck,
              compiledFor: compiledPdfFilePathRef.current,
            }
          );
          setIsPdfStale(true);
          setIsPdfForDifferentFile(true);
        }
      }
    };

    ensurePdf();

    return () => {
      cancelled = true;
    };
  }, [
    currentDocument,
    content,
    compiledPdfBlob,
    restorePersistedPdfBlob,
    getActiveTab,
    isWaitingForZipImport,
    handleCompile,
  ]);

  // Update title value when document changes
  useEffect(() => {
    if (currentDocument && !isEditingTitle) {
      setTitleValue(currentDocument.title);
    }
  }, [currentDocument, isEditingTitle]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+S or Cmd+S - prevent browser save dialog (auto-save handles saving)
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        // Auto-save is always enabled, no manual save needed
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Filter view mode options based on document type
  const viewModeOptions = React.useMemo(() => {
    const allOptions = [
      { id: "editor" as ViewMode, label: "Editor", icon: CodeBracketIcon },
      { id: "split" as ViewMode, label: "Split", icon: DocumentDuplicateIcon },
      { id: "preview" as ViewMode, label: "Preview", icon: EyeIcon },
    ];

    // Word documents only support editor mode (WYSIWYG)
    if (currentDocument?.document_type === "docx") {
      return allOptions.filter((option) => option.id === "editor");
    }

    return allOptions;
  }, [currentDocument?.document_type]);

  // Show pending share prompt if document is shared with email but user needs to register
  if (pendingShare && documentId) {
    return (
      <>
        <SharedDocumentPrompt
          documentId={documentId}
          email={pendingShare.email}
          onClose={() => {
            setPendingShare(null)
            navigate('/home')
          }}
        />
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-gray-600 dark:text-gray-400">
              Checking document access...
            </p>
          </div>
        </div>
      </>
    )
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Resizable Toolbar */}
      <PanelGroup direction="vertical" className="flex-shrink-0">
        <Panel
          id="toolbar-panel"
          defaultSize={4}
          minSize={3}
          maxSize={5}
          collapsible={false}
          className="flex flex-col"
        >
          {/* Compact Overleaf-style header - Redesigned */}
          <div className="flex items-center justify-between px-3 py-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 h-full overflow-hidden">
            {/* Left Section: Title, Toolbar, Type - Scrollable */}
            <div
              className="flex items-center gap-2 flex-1 min-w-0 overflow-x-auto overflow-y-hidden toolbar-scroll"
              style={{
                WebkitOverflowScrolling: "touch",
              }}
            >
              {/* Document Title */}
              {currentDocument && (
                <>
                  <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0 min-w-0 max-w-[140px] sm:max-w-[200px] lg:max-w-none">
                    {isEditingTitle ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          value={titleValue}
                          onChange={(e) => setTitleValue(e.target.value)}
                          onBlur={handleTitleSave}
                          onKeyDown={handleTitleKeyPress}
                          disabled={isSavingTitle}
                          className="px-2 py-0.5 text-sm font-medium bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-2 border-primary-500 dark:border-primary-400 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-600 shadow-sm disabled:opacity-50 w-full"
                          autoFocus
                          placeholder="Enter document title..."
                        />
                        {isSavingTitle && (
                          <div className="w-3 h-3 border-2 border-primary-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={handleTitleEdit}
                        className="group flex items-center gap-1.5 px-1.5 py-0.5 text-sm font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors truncate"
                        title="Click to edit document title"
                      >
                        <span className="truncate">
                          {currentDocument.title}
                        </span>
                        <PencilIcon className="w-3 h-3 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors flex-shrink-0" />
                      </button>
                    )}
                  </div>
                  <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
                </>
              )}

              {/* LaTeX Toolbar - Scrollable on small screens */}
              {currentDocument?.document_type === "latex" &&
                mode === "latex" && (
                  <>
                    {/* LaTeX Editor Toolbar */}
                    {mode === "latex" && (
                      <>
                        <div
                          className="flex-shrink-0"
                          style={{
                            width: "max-content",
                            minWidth: "max-content",
                            flexShrink: 0,
                          }}
                        >
                          <LaTeXToolbar
                            view={editorView}
                            theme={effectiveTheme === "dark" ? "dark" : "light"}
                            documentContent={content}
                            documentId={documentId}
                          />
                        </div>
                        <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
                      </>
                    )}
                  </>
                )}

              {/* Document Type Badge */}
              <div className="hidden sm:flex items-center px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded flex-shrink-0 whitespace-nowrap">
                {currentDocument?.document_type === "latex" && "LaTeX"}
                {currentDocument?.document_type === "markdown" && "Markdown"}
                {currentDocument?.document_type === "text" && "Plain Text"}
                {currentDocument?.document_type === "docx" && "Word Document"}
                {!currentDocument?.document_type && "Document"}
              </div>
            </div>

            {/* Right Section: Actions & Status */}
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-1 sm:ml-2 min-w-0 sm:min-w-[200px] justify-end">
              {/* Research Tools starts */}
              <div className="hidden md:flex items-center gap-1">
                <button
                  onClick={() =>
                    setSidePanel(sidePanel === "citations" ? null : "citations")
                  }
                  className={clsx(
                    "flex items-center px-2 py-1.5 text-xs font-medium rounded transition-colors",
                    sidePanel === "citations"
                      ? "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-300"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                  )}
                  title="Citation Library"
                >
                  <BookmarkIcon className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Citations</span>
                </button>

                <button
                  onClick={() =>
                    setSidePanel(
                      sidePanel === "literature" ? null : "literature"
                    )
                  }
                  className={clsx(
                    "flex items-center px-2 py-1.5 text-xs font-medium rounded transition-colors",
                    sidePanel === "literature"
                      ? "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-300"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                  )}
                  title="Discover Literature"
                >
                  <BookOpenIcon className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Discover</span>
                </button>

                <button
                  onClick={() =>
                    setSidePanel(sidePanel === "draft" ? null : "draft")
                  }
                  className={clsx(
                    "flex items-center px-2 py-1.5 text-xs font-medium rounded transition-colors",
                    sidePanel === "draft"
                      ? "bg-purple-100 text-purple-900 dark:bg-purple-900/30 dark:text-purple-300"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                  )}
                  title="Generate Section"
                >
                  <DocumentTextIcon className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Generate</span>
                </button>

                <button
                  onClick={() =>
                    setSidePanel(sidePanel === "review" ? null : "review")
                  }
                  className={clsx(
                    "flex items-center px-2 py-1.5 text-xs font-medium rounded transition-colors",
                    sidePanel === "review"
                      ? "bg-cyan-100 text-cyan-900 dark:bg-cyan-900/30 dark:text-cyan-300"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                  )}
                  title="Review - Comments, History & Track Changes"
                >
                  <ChatBubbleLeftRightIcon className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Review</span>
                </button>
              </div>
              {/* Research Tools ends */}

              {/* Track Changes Toggle */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setTrackChangesEnabled(!trackChangesEnabled)}
                  className={clsx(
                    "flex items-center px-2 py-1.5 text-xs font-medium rounded transition-colors",
                    trackChangesEnabled
                      ? "bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-300"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                  )}
                  title={trackChangesEnabled ? "Track Changes: ON" : "Track Changes: OFF"}
                >
                  <PencilSquareIcon className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">
                    {trackChangesEnabled ? "Track On" : "Track Off"}
                  </span>
                </button>
              </div>

              <div className="flex items-center gap-1">
                {isSavingFile && (() => {
                  const activeTab = getActiveTab();
                  return activeTab && (
                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mr-2">
                      <ArrowPathIcon className="w-3.5 h-3.5 mr-1 animate-spin" />
                      <span className="hidden sm:inline">
                        Saving {activeTab.file.name}…
                      </span>
                    </div>
                  );
                })()}

                <div className="flex items-center gap-1">
                  {/* AI Model Selector - Distinct styling */}
                  {aiPreferences && (
                    <div className="relative">
                      <button
                        ref={aiModelButtonRef}
                        type="button"
                        onClick={() => setIsAIMenuOpen((open) => !open)}
                        className={clsx(
                          "flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200",
                          "bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-500 dark:to-indigo-500",
                          "text-white shadow-md hover:shadow-lg",
                          "hover:from-purple-700 hover:to-indigo-700 dark:hover:from-purple-600 dark:hover:to-indigo-600",
                          "border-2 border-purple-400 dark:border-purple-300",
                          isAIMenuOpen && "ring-2 ring-purple-300 dark:ring-purple-200 ring-offset-2"
                        )}
                        title="Select AI Model"
                      >
                        <SparklesIcon className="w-4 h-4" />
                        <span className="hidden sm:inline truncate max-w-[120px] font-semibold">
                          {aiModel || aiProvider || "AI"}
                        </span>
                        <ChevronRightIcon className={clsx(
                          "w-3 h-3 flex-shrink-0 transition-transform",
                          isAIMenuOpen ? "rotate-90" : "-rotate-90"
                        )} />
                      </button>

                      {isAIMenuOpen && createPortal(
                        <>
                          {/* Backdrop */}
                          <div
                            className="fixed inset-0 z-[9998] bg-black/20 backdrop-blur-sm"
                            onClick={() => setIsAIMenuOpen(false)}
                          />

                          {/* Dropdown - Portal to body */}
                          <div
                            className={clsx(
                              "fixed",
                              "bottom-0 left-0 right-0 sm:bottom-auto sm:left-auto",
                              "w-full sm:w-64",
                              "max-h-[70vh] sm:max-h-[400px]",
                              "bg-white dark:bg-gray-900",
                              "rounded-t-2xl sm:rounded-lg",
                              "shadow-2xl border-t sm:border border-gray-200 dark:border-gray-700",
                              "z-[9999] overflow-hidden"
                            )}
                            style={{
                              ...(typeof window !== 'undefined' && window.innerWidth >= 640 && dropdownPosition && {
                                top: `${dropdownPosition.top}px`,
                                right: `${dropdownPosition.right}px`,
                                left: 'auto',
                                bottom: 'auto',
                              })
                            }}>
                            <div className="p-3 space-y-3">
                              <div>
                                <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                  Provider
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {aiPreferences.available_providers.map((provider) => (
                                    <button
                                      key={provider}
                                      type="button"
                                      onClick={async () => {
                                        setAiProvider(provider);
                                        setAiModel(null);
                                        try {
                                          await aiProvidersAPI.updatePreferences({ provider, model: null });
                                          queryClient.invalidateQueries({ queryKey: ["aiPreferences"] });
                                        } catch (error) {
                                          logError("Error updating AI provider:", error);
                                        }
                                      }}
                                      className={clsx(
                                        "px-2 py-1 rounded-md text-[11px]",
                                        aiProvider === provider
                                          ? "bg-purple-600 text-white"
                                          : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
                                      )}
                                    >
                                      {provider}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                  Model
                                </div>
                                <select
                                  className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs text-gray-800 dark:text-gray-100 px-2 py-1"
                                  value={aiModel || ""}
                                  onChange={async (e) => {
                                    const model = e.target.value || null;
                                    setAiModel(model);
                                    setIsAIMenuOpen(false);
                                    try {
                                      await aiProvidersAPI.updatePreferences({
                                        provider: aiProvider || undefined,
                                        model,
                                      });
                                      queryClient.invalidateQueries({ queryKey: ["aiPreferences"] });
                                    } catch (error) {
                                      logError("Error updating AI model:", error);
                                    }
                                  }}
                                >
                                  <option value="">Default</option>
                                  {(aiPreferences.provider_models[aiProvider || ""] ||
                                    []).map((model) => (
                                      <option key={model} value={model}>
                                        {model}
                                      </option>
                                    ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        </>,
                        document.body
                      )}
                    </div>
                  )}

                  {/* Separator */}
                  <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

                  {/* Share Button */}
                  {currentDocument && (
                    <button
                      onClick={() => setShareModalOpen(true)}
                      className="flex items-center justify-center p-2 text-sm font-medium transition-colors text-gray-600 hover:text-gray-900 bg-gray-100 dark:bg-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded"
                      title="Share document"
                    >
                      <ShareIcon className="w-4 h-4" />
                    </button>
                  )}

                  {/* Export Button */}
                  {mode === "latex" && (
                    <div className="flex items-center bg-gray-100 border-l border-gray-300 dark:border-gray-600 dark:bg-gray-700 pl-2 ml-2">
                      <button
                        onClick={async () => {
                          if (!currentDocument) return;
                          setIsExporting(true);
                          try {
                            const blob = await latexAPI.exportProject(
                              currentDocument.id
                            );

                            // Create a blob URL and download link
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `${currentDocument.title || "project"
                              }.zip`;
                            a.style.display = "none";

                            // Append to DOM (required for some browsers)
                            document.body.appendChild(a);

                            // Trigger download
                            a.click();

                            // Cleanup: remove link and revoke URL after a short delay
                            setTimeout(() => {
                              document.body.removeChild(a);
                              URL.revokeObjectURL(url);
                            }, 100);
                          } catch (error: any) {
                            logError("Export error:", error);
                          } finally {
                            setIsExporting(false);
                          }
                        }}
                        disabled={isExporting}
                        className="flex items-center px-2 py-1.5 text-xs font-medium  transition-colors text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
                        title="Export project as ZIP"
                      >
                        {isExporting ? (
                          <>
                            <ArrowPathIcon className="w-3.5 h-3.5 sm:mr-1.5 animate-spin" />
                            <span className="hidden sm:inline">
                              Exporting...
                            </span>
                          </>
                        ) : (
                          <>
                            <ArrowDownTrayIcon className="w-3.5 h-3.5 sm:mr-1.5" />
                            <span className="hidden sm:inline">Export</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Panel>

        {/* Resize Handle */}
        <PanelResizeHandle className="h-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors cursor-row-resize" />

        {/* Main Editor Area */}
        <Panel
          id="editor-area-panel"
          defaultSize={92}
          minSize={20}
          className="flex-1 overflow-hidden"
        >
          <div className="relative flex h-full flex-col">
            {/* Loading overlay when document is loading or ZIP is being imported */}
            {(isLoadingDocument || isWaitingForZipImport) && (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-white bg-opacity-90 dark:bg-gray-900 dark:bg-opacity-90">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {isWaitingForZipImport
                      ? "Processing uploaded files, please wait..."
                      : "Loading document..."}
                  </p>
                  {isWaitingForZipImport && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Ensuring your files are ready before opening the editor...
                    </p>
                  )}
                </div>
              </div>
            )}
            <div className="relative flex-1 overflow-hidden flex flex-row">
              <div className="flex-1 min-w-0 h-full relative">
                <PanelGroup
                  direction={isMobile ? "vertical" : "horizontal"}
                  className="h-full"
                >
                  {/* File Browser (only for LaTeX documents) - Always render Panel but conditionally show content */}
                  {currentDocument?.document_type === "latex" && (
                    <>
                      <Panel
                        id="file-browser-panel"
                        order={1}
                        ref={fileBrowserPanelRef}
                        defaultSize={showFileBrowser ? 12 : 0}
                        minSize={0}
                        maxSize={20}
                        collapsible={true}
                        className="overflow-visible"
                      >
                        {showFileBrowser && (
                          <FileBrowser
                            documentId={currentDocument.id}
                            refreshToken={fileBrowserRefreshToken}
                            onFileSelect={async (file) => {
                              logDebug("[EditorPage] File selected:", {
                                id: file.id,
                                name: file.name,
                                path: file.file_path,
                              });
                              
                              // Save current tab before switching
                              await flushPendingFileSave({ silent: true });

                              // Check if it's a text file that should be opened in a tab
                              if (
                                file.file_type === "file" &&
                                (file.mime_type?.startsWith("text/") ||
                                  file.name.endsWith(".tex") ||
                                  file.name.endsWith(".bib") ||
                                  file.name.endsWith(".sty") ||
                                  file.name.endsWith(".cls"))
                              ) {
                                // Add or switch to tab
                                await addOrSwitchToTab(file);
                                
                                // Handle .tex file PDF stale logic
                                try {
                                  if (file.name.endsWith(".tex")) {
                                    const blob = await latexAPI.getFileContent(
                                      currentDocument.id,
                                      file.id
                                    );
                                    const text = await blob.text();
                                    const currentHash = computeContentHash(text);
                                    
                                    if (
                                      lastTexContentHashRef.current &&
                                      lastTexContentHashRef.current !== currentHash
                                    ) {
                                      logDebug(
                                        "[EditorPage] .tex content changed, marking PDF as stale",
                                        {
                                          lastHash: lastTexContentHashRef.current,
                                          currentHash: currentHash,
                                        }
                                      );
                                      setIsPdfStale(true);
                                      lastTexContentHashRef.current = currentHash;
                                    } else if (!lastTexContentHashRef.current) {
                                      lastTexContentHashRef.current = currentHash;
                                      if (
                                        compiledContentHashRef.current &&
                                        compiledContentHashRef.current !== currentHash
                                      ) {
                                        setIsPdfStale(true);
                                      } else {
                                        setIsPdfStale(false);
                                      }
                                    } else {
                                      setIsPdfStale(false);
                                    }
                                  }
                                } catch (error: any) {
                                  logError(
                                    "[EditorPage] Failed to check .tex file hash:",
                                    error
                                  );
                                }
                              }
                            }}
                            onFileCreate={(_file) => {
                              triggerFileBrowserRefresh();
                            }}
                            onFileDelete={(fileId) => {
                              // Close tab if it's open
                              const tabExists = openTabs.find(tab => tab.file.id === fileId);
                              if (tabExists) {
                                closeTab(fileId);
                              }
                            }}
                            onCollapse={() => {
                              setIsFileBrowserCollapsed(true);
                              setShowFileBrowser(false);
                              fileBrowserPanelRef.current?.collapse();
                            }}
                          />
                        )}
                      </Panel>
                      {/* Always render resize handle - it will be functional even when file browser is collapsed */}
                      <PanelResizeHandle
                        className="w-2 bg-transparent hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors cursor-col-resize"
                        style={{
                          opacity:
                            showFileBrowser && !isFileBrowserCollapsed ? 1 : 0.3,
                        }}
                      />
                      {/* Expand button when collapsed - Always visible when file browser is hidden */}
                      {!showFileBrowser && (
                        <button
                          onClick={() => {
                            setShowFileBrowser(true);
                            setIsFileBrowserCollapsed(false);
                            // Resize both panels: expand file browser to 12%, shrink editor to 88%
                            setTimeout(() => {
                              if (
                                fileBrowserPanelRef.current &&
                                editorPanelRef.current
                              ) {
                                fileBrowserPanelRef.current.resize(12);
                                editorPanelRef.current.resize(88);
                              } else if (fileBrowserPanelRef.current) {
                                fileBrowserPanelRef.current.expand();
                              }
                            }, 50);
                          }}
                          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 border-r border-gray-300 dark:border-gray-600 rounded-r-md shadow-sm transition-colors"
                          title="Expand file browser"
                        >
                          <ChevronRightIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </button>
                      )}
                    </>
                  )}

                  {/* Editor Panel */}
                  <Panel
                    id="main-editor-panel"
                    order={showFileBrowser && !isFileBrowserCollapsed ? 2 : 1}
                    ref={editorPanelRef}
                    defaultSize={
                      sidePanel
                        ? showFileBrowser && !isFileBrowserCollapsed
                          ? 68
                          : 75
                        : showFileBrowser && !isFileBrowserCollapsed
                          ? 80
                          : 100
                    }
                    minSize={30}
                    maxSize={
                      sidePanel
                        ? showFileBrowser && !isFileBrowserCollapsed
                          ? 80
                          : 85
                        : undefined
                    }
                    className="relative"
                  >
                    <PanelGroup direction={isMobile ? "vertical" : "horizontal"}>
                      {/* Left: Editor */}
                      <Panel
                        id="editor-content-panel"
                        order={1}
                        defaultSize={viewMode === "split" && !isMobile ? 50 : 100}
                        minSize={isMobile ? 50 : 30}
                        className="relative flex flex-col"
                      >
                        {/* Tab Bar - only show for LaTeX documents with open tabs */}
                        {currentDocument?.document_type === "latex" && openTabs.length > 0 && (
                          <TabBar
                            tabs={openTabs}
                            activeTabId={activeTabId}
                            onTabClick={switchToTab}
                            onTabClose={closeTab}
                          />
                        )}
                        <div className="flex-1 overflow-hidden">
                          <ErrorBoundary>
                          {mode === "word" ? (
                            <WordEditor className="h-full" />
                          ) : (
                            // Source Editor (CodeMirror)
                            <ScientificEditor
                              onOpenReviewPanel={() => setSidePanel("review")}
                              key={`editor-${currentDocument?.id}`}
                              value={content}
                              onChange={handleContentChange}
                              onViewReady={(view) => setEditorView(view)}
                              highlightLine={highlightLine}
                              onNavigateToLine={(lineNumber) => {
                                // Navigation is handled by highlightLine prop
                                logDebug('[EditorPage] Navigate to line requested:', lineNumber)
                              }}
                              onSave={async () => {
                                // Save document
                                if (currentDocument && canEdit) {
                                  const activeTab = getActiveTab();
                                  // For LaTeX documents with an active file, always save directly to that file
                                  // This ensures all files (including main.tex) are saved individually
                                  if (
                                    currentDocument.document_type === "latex" &&
                                    activeTab
                                  ) {
                                    // CRITICAL: Get the current content directly from the editor view
                                    // This ensures we save the latest content, not stale state
                                    const currentEditorContent = editorView?.state.doc.toString() || content;
                                    
                                    // Save to the active file (main.tex, major.tex, chapter1.tex, etc.)
                                    // First ensure the pending save is set up with current content from editor
                                    pendingFileSaveRef.current = {
                                      fileId: activeTab.file.id,
                                      content: currentEditorContent,
                                    };
                                    // Flush immediately (bypass debounce) and force version creation for manual saves
                                    await flushPendingFileSave({ forceVersion: true });
                                    // Update tab's lastSavedContent and isDirty
                                    setOpenTabs(prev => prev.map(tab => 
                                      tab.file.id === activeTab.file.id
                                        ? { ...tab, lastSavedContent: content, isDirty: false }
                                        : tab
                                    ));
                                    
                                    // CRITICAL: Trigger auto-compilation after manual save (Cmd+S)
                                    // Only compile if it's a .tex file
                                    if (activeTab.file.file_path.endsWith(".tex")) {
                                      // Small delay to ensure save completes
                                      setTimeout(() => {
                                        handleCompile({ silent: true });
                                      }, 300);
                                    }
                                  } else {
                                    // Non-LaTeX documents or no active file - use document update path
                                    await triggerAutosave(content);
                                    if (activeTab) {
                                      setOpenTabs(prev => prev.map(tab => 
                                        tab.file.id === activeTab.file.id
                                          ? { ...tab, lastSavedContent: content, isDirty: false }
                                          : tab
                                      ));
                                    }
                                  }
                                }
                              }}
                              onSaveAndCompile={async () => {
                                // Save first, then compile
                                if (currentDocument && canEdit) {
                                  const activeTab = getActiveTab();
                                  // For LaTeX documents with an active file, always save directly to that file
                                  // This ensures all files (including main.tex) are saved individually
                                  if (
                                    currentDocument.document_type === "latex" &&
                                    activeTab
                                  ) {
                                    // Save to the active file (main.tex, major.tex, chapter1.tex, etc.)
                                    // First ensure the pending save is set up with current content
                                    pendingFileSaveRef.current = {
                                      fileId: activeTab.file.id,
                                      content: content,
                                    };
                                    // Flush immediately (bypass debounce)
                                    await flushPendingFileSave();
                                    // Update tab's lastSavedContent and isDirty
                                    setOpenTabs(prev => prev.map(tab => 
                                      tab.file.id === activeTab.file.id
                                        ? { ...tab, lastSavedContent: content, isDirty: false }
                                        : tab
                                    ));
                                  } else {
                                    // Non-LaTeX documents or no active file - use document update path
                                    await triggerAutosave(content);
                                    if (activeTab) {
                                      setOpenTabs(prev => prev.map(tab => 
                                        tab.file.id === activeTab.file.id
                                          ? { ...tab, lastSavedContent: content, isDirty: false }
                                          : tab
                                      ));
                                    }
                                  }
                                  // Small delay to ensure save completes
                                  setTimeout(() => {
                                    handleCompile();
                                  }, 300);
                                }
                              }}
                              onCursorChange={(position: number) => {
                                // Send cursor position update via WebSocket
                                if (isWsConnected && wsSendCursorUpdate) {
                                  wsSendCursorUpdate(position);
                                }
                              }}
                              remoteCursors={remoteCursors.filter(
                                (c) => c.userId !== currentUser?.id
                              )} // Don't show own cursor
                              documentId={currentDocument?.id} // Pass document ID for authorship tracking
                              blameEnabled={blameEnabled} // Pass blame toggle state
                              mode={mode}
                              readOnly={!canEdit}
                              className="h-full"
                            />
                          )}
                          {!canEdit && (
                            <div className="absolute top-4 left-4 right-4 bg-yellow-50 border border-yellow-200 rounded-md px-4 py-2 text-sm text-yellow-800 z-10">
                              <div className="flex items-center gap-2">
                                <EyeIcon className="w-4 h-4" />
                                <span>
                                  {permission === "viewer"
                                    ? "You are viewing this document in read-only mode."
                                    : permission === "reviewer"
                                      ? "You can view and add comments, but cannot edit the content."
                                      : "You do not have permission to edit this document."}
                                </span>
                              </div>
                            </div>
                          )}
                          </ErrorBoundary>
                        </div>
                      </Panel>

                      {/* Split view divider */}
                      {viewMode === "split" && !isMobile && (
                        <PanelResizeHandle
                          className={clsx(
                            "bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors",
                            isMobile ? "h-2 cursor-row-resize" : "w-2 cursor-col-resize"
                          )}
                          style={{ touchAction: "none" }}
                        />
                      )}

                      {/* Right: Preview (in split mode) */}
                      {viewMode === "split" && !isMobile && (
                        <Panel
                          id="preview-panel"
                          order={2}
                          defaultSize={50}
                          minSize={30}
                        >
                          <ErrorBoundary>
                            <div className="relative h-full">
                              <div className="flex flex-col h-full">
                                <div className="flex-1 min-h-0">
                                  <DocumentPreview
                                    content={content}
                                    mode={
                                      currentDocument?.document_type === "latex"
                                        ? "latex"
                                        : currentDocument?.document_type ===
                                          "markdown"
                                          ? "markdown"
                                          : "plain"
                                    }
                                    documentId={currentDocument?.id}
                                    documentType={currentDocument?.document_type}
                                    currentFilePath={getActiveTab()?.file.file_path}
                                    autoCompile={false}
                                    compiledPdfBlob={compiledPdfBlob}
                                    compilationErrors={compilationErrors}
                                    compilationWarnings={compilationWarnings}
                                    onToggleDebugPanel={() =>
                                      setShowDebugPanel(!showDebugPanel)
                                    }
                                    isDebugPanelOpen={showDebugPanel}
                                    navigateToPdfPage={navigateToPdfPage}
                                    // CRITICAL: Use the file path that the PDF was compiled for, not the currently active file
                                    // This ensures SyncTeX uses the correct .synctex.gz file
                                    mainFilePathForSyncTeX={compiledPdfFilePathRef.current || undefined}
                                    onNavigateToSource={async (filePath: string, line: number, column: number) => {
                                      console.log('[EditorPage] Navigate to source from PDF:', { filePath, line, column })
                                      
                                      // Find or open the file tab
                                      const fileTree = await latexAPI.getFileTree(currentDocument!.id)
                                      console.log('[EditorPage] File tree:', fileTree.files?.map((f: any) => ({ id: f.id, file_path: f.file_path, name: f.name })))
                                      
                                      // Normalize paths for comparison (remove leading/trailing slashes, handle both absolute and relative)
                                      const normalizePath = (path: string): string => {
                                        return path.replace(/^\/+/, '').replace(/\/+$/, '')
                                      }
                                      
                                      // Find file by path in file tree
                                      const findFileByPath = (files: any[], targetPath: string): any => {
                                        const normalizedTarget = normalizePath(targetPath)
                                        console.log('[EditorPage] Searching for normalized path:', normalizedTarget)
                                        
                                        for (const file of files) {
                                          const normalizedFile = normalizePath(file.file_path || '')
                                          
                                          // Try exact match
                                          if (normalizedFile === normalizedTarget) {
                                            console.log('[EditorPage] Found exact match:', file.file_path)
                                            return file
                                          }
                                          
                                          // Try matching by filename (last part of path)
                                          const targetFileName = normalizedTarget.split('/').pop()
                                          const fileFileName = normalizedFile.split('/').pop()
                                          if (targetFileName && fileFileName === targetFileName) {
                                            console.log('[EditorPage] Found filename match:', file.file_path, 'for', targetFileName)
                                            return file
                                          }
                                          
                                          // Try endsWith match
                                          if (normalizedFile.endsWith('/' + normalizedTarget) || normalizedTarget.endsWith('/' + normalizedFile)) {
                                            console.log('[EditorPage] Found endsWith match:', file.file_path)
                                            return file
                                          }
                                          
                                          // Recursively search children
                                          if (file.children && file.children.length > 0) {
                                            const found = findFileByPath(file.children, targetPath)
                                            if (found) return found
                                          }
                                        }
                                        return null
                                      }
                                      
                                      const targetFile = findFileByPath(fileTree.files || [], filePath)
                                      
                                      console.log('[EditorPage] SyncTeX file lookup result:', { 
                                        filePath, 
                                        found: !!targetFile,
                                        targetFile: targetFile ? { id: targetFile.id, file_path: targetFile.file_path, name: targetFile.name } : null
                                      })
                                      
                                      if (targetFile && targetFile.file_type === 'file') {
                                        // Check if file is already open in a tab
                                        const existingTab = openTabs.find(tab => tab.file.id === targetFile.id)
                                        
                                        if (existingTab) {
                                          // Switch to existing tab
                                          console.log('[EditorPage] Switching to existing tab:', existingTab.file.id)
                                          switchToTab(existingTab.file.id)
                                        } else {
                                          // Load file content and open new tab
                                          console.log('[EditorPage] Opening new tab for file:', targetFile.id, targetFile.name)
                                          try {
                                            const fileContent = await latexAPI.getFileContent(currentDocument!.id, targetFile.id)
                                            const textContent = await fileContent.text()
                                            
                                            // Add new tab - addOrSwitchToTab expects DocumentFile and optional content
                                            await addOrSwitchToTab(targetFile, textContent)
                                            console.log('[EditorPage] Tab opened successfully')
                                          } catch (error) {
                                            console.error('[EditorPage] Failed to load file for SyncTeX navigation:', error)
                                            return
                                          }
                                        }
                                        
                                        // Wait a bit for tab to be ready, then navigate to line
                                        setTimeout(() => {
                                          console.log('[EditorPage] Setting highlight line:', { lineNumber: line, type: 'warning' })
                                          setHighlightLine({ lineNumber: line, type: 'warning' })
                                          // Clear highlight after 5 seconds
                                          setTimeout(() => {
                                            console.log('[EditorPage] Clearing highlight line')
                                            setHighlightLine(null)
                                          }, 5000)
                                        }, 300)
                                      } else {
                                        console.error('[EditorPage] File not found for SyncTeX navigation:', { 
                                          filePath, 
                                          fileTreeFiles: fileTree.files?.map((f: any) => ({ id: f.id, file_path: f.file_path, name: f.name }))
                                        })
                                      }
                                    }}
                                  />
                                </div>
                                {/* LaTeX Debug Panel - shows errors and warnings */}
                                {currentDocument?.document_type === "latex" && (
                                  <LaTeXDebugPanel
                                    errors={compilationErrors}
                                    warnings={compilationWarnings}
                                    isOpen={showDebugPanel}
                                    onToggle={() =>
                                      setShowDebugPanel(!showDebugPanel)
                                    }
                                    onClose={() => {
                                      setShowDebugPanel(false);
                                      // Clear highlight when closing panel
                                      setHighlightLine(null);
                                      setNavigateToPdfPage(null);
                                      // Clear errors/warnings when closing (optional - user might want to keep them)
                                      // setCompilationErrors([])
                                      // setCompilationWarnings([])
                                    }}
                                    onWarningClick={(lineNumber, pageNumber, message) => {
                                      logDebug('[EditorPage] Warning clicked:', { lineNumber, pageNumber, message });
                                      if (lineNumber !== null) {
                                        setHighlightLine({ lineNumber, type: 'warning' });
                                        // Clear highlight after 5 seconds
                                        setTimeout(() => setHighlightLine(null), 5000);
                                      }
                                      if (pageNumber !== null) {
                                        setNavigateToPdfPage(pageNumber);
                                        // Keep navigation persistent - don't clear it automatically
                                      }
                                    }}
                                    onErrorClick={(lineNumber, pageNumber, message) => {
                                      logDebug('[EditorPage] Error clicked:', { lineNumber, pageNumber, message });
                                      if (lineNumber !== null) {
                                        setHighlightLine({ lineNumber, type: 'error' });
                                        // Clear highlight after 5 seconds
                                        setTimeout(() => setHighlightLine(null), 5000);
                                      }
                                      if (pageNumber !== null) {
                                        setNavigateToPdfPage(pageNumber);
                                        // Keep navigation persistent - don't clear it automatically
                                      }
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                          </ErrorBoundary>
                        </Panel>
                      )}
                    </PanelGroup>
                  </Panel>

                  {/* Side Panel */}
                  {sidePanel && (
                    <>
                      <PanelResizeHandle className="w-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors cursor-col-resize" />
                      <Panel
                        id="side-panel"
                        order={showFileBrowser && !isFileBrowserCollapsed ? 3 : 2}
                        defaultSize={
                          showFileBrowser && !isFileBrowserCollapsed ? 20 : 25
                        }
                        minSize={15}
                        maxSize={
                          showFileBrowser && !isFileBrowserCollapsed ? 50 : 60
                        }
                      >
                        <ErrorBoundary>
                          {sidePanel === "latex" && (
                            <LatexCompilePanel
                              content={content}
                              onContentChange={handleContentChange}
                            />
                          )}
                          {sidePanel === "citations" && currentDocument && (
                            <CitationLibraryPanel
                              documentId={currentDocument.id}
                              documentMode={
                                mode === "latex"
                                  ? "latex"
                                  : mode === "markdown"
                                    ? "markdown"
                                    : "plain"
                              }
                              onCitationInsert={handleCitationInsert}
                              isOpen={sidePanel === "citations"}
                              onClose={() => setSidePanel(null)}
                            />
                          )}
                          {sidePanel === "literature" && currentDocument && (
                            <LiteratureDiscoveryPanel
                              documentId={currentDocument.id}
                              isOpen={sidePanel === "literature"}
                              onClose={() => setSidePanel(null)}
                              onCitationInsert={handleCitationInsert}
                            />
                          )}
                          {sidePanel === "draft" && currentDocument && (
                            <DraftGenerator
                              documentId={currentDocument.id}
                              documentContent={content}
                              cursorPosition={cursorPosition}
                              onContentInsert={(newContent, position) => {
                                // Insert content at cursor position or at the end
                                if (position !== undefined && position >= 0) {
                                  // Insert at specific position
                                  const before = content.substring(0, position);
                                  const after = content.substring(position);
                                  handleContentChange(
                                    before + "\n\n" + newContent + "\n\n" + after
                                  );
                                } else {
                                  // Insert at the end
                                  handleContentChange(
                                    content + "\n\n" + newContent
                                  );
                                }
                              }}
                              onClose={() => setSidePanel(null)}
                              isOpen={sidePanel === "draft"}
                            />
                          )}
                          {sidePanel === "review" && currentDocument && (
                            <ReviewSidebar
                              isOpen={sidePanel === "review"}
                              onClose={() => setSidePanel(null)}
                              documentId={currentDocument.id}
                              currentFileId={getActiveTab()?.file.id || null}
                              currentFileName={getActiveTab()?.file.name || null}
                              onFileRestored={async () => {
                                // Refresh the active file content
                                await refreshActiveFileContent({ silent: true })
                                // Trigger compilation if it's a .tex file
                                const activeTab = getActiveTab()
                                if (activeTab?.file.file_path.endsWith('.tex')) {
                                  setTimeout(() => {
                                    handleCompile({ silent: true })
                                  }, 300)
                                }
                              }}
                            />
                          )}
                        </ErrorBoundary>
                      </Panel>
                    </>
                  )}
                </PanelGroup>
              </div>
            </div>

            {/* Preview Mode (full screen) */}
            {viewMode === "preview" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 z-40 bg-white dark:bg-gray-900"
              >
                <div className="flex h-full flex-col">
                  <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Preview
                    </h2>
                    <button
                      onClick={() => setViewMode("editor")}
                      className="rounded-md px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                    >
                      Back to Editor
                    </button>
                  </div>
                  <div className="flex-1 overflow-hidden flex flex-col">
                    <ErrorBoundary>
                      <div className="relative flex-1 min-h-0 flex flex-col">
                        <div className="flex-1 min-h-0">
                          <DocumentPreview
                            content={content}
                            mode={mode === 'word' ? 'plain' : mode}
                            documentId={currentDocument?.id}
                            documentType={currentDocument?.document_type}
                            currentFilePath={getActiveTab()?.file.file_path}
                            autoCompile={false}
                            compiledPdfBlob={compiledPdfBlob}
                            compilationErrors={compilationErrors}
                            compilationWarnings={compilationWarnings}
                            onToggleDebugPanel={() =>
                              setShowDebugPanel(!showDebugPanel)
                            }
                            isDebugPanelOpen={showDebugPanel}
                            mainFilePathForSyncTeX={compiledPdfFilePathRef.current || getActiveTab()?.file.file_path || undefined}
                          />
                        </div>
                        {/* LaTeX Debug Panel - shows errors and warnings */}
                        {currentDocument?.document_type === "latex" && (
                          <LaTeXDebugPanel
                            errors={compilationErrors}
                            warnings={compilationWarnings}
                            isOpen={showDebugPanel}
                            onToggle={() =>
                              setShowDebugPanel(!showDebugPanel)
                            }
                            onClose={() => {
                              setShowDebugPanel(false);
                            }}
                          />
                        )}
                      </div>
                    </ErrorBoundary>
                  </div>
                </div>
              </motion.div>
            )}

            {/* secondary editor footer begins */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between max-w-[900px] w-full mx-auto gap-2 sm:gap-4 flex-wrap px-2 sm:px-4 py-2 sm:py-1">
              {/* Daily AI credits indicator */}
              <div className="hidden sm:flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                {userTokenStats && (userTokenStats as any).daily_tokens_total ? (
                  (() => {
                    const stats = userTokenStats as any;
                    const total = stats.daily_tokens_total || 0;
                    const remaining = stats.daily_tokens_remaining ?? 0;
                    const pctRemaining =
                      total > 0 ? Math.max(0, Math.min(100, Math.round((remaining / total) * 100))) : 0;
                    return (
                      <>
                        <span>Daily AI credits</span>
                        <div className="w-20 lg:w-24 h-2 rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
                          <div
                            className="h-2 rounded-full bg-gradient-to-r from-teal-500 to-cyan-500"
                            style={{ width: `${pctRemaining}%` }}
                          />
                        </div>
                        <span>
                          {remaining}/{total}
                        </span>
                      </>
                    );
                  })()
                ) : (
                  <span>Loading credits…</span>
                )}
              </div>
              {/* tabs (editor/split/preview) + Compile button in same row */}
              <div className="flex items-center gap-2 justify-center w-full">
                <div className="flex justify-center items-center max-w-full sm:max-w-[300px] w-full rounded-lg border border-gray-300 dark:border-gray-600 p-0.5 sm:p-1">
                  {viewModeOptions
                    .filter(option => !isMobile || option.id !== 'split') // Hide split on mobile
                    .map((option) => (
                      <button
                        key={option.id}
                        onClick={() => {
                          setViewMode(option.id);
                          // If switching to preview mode and no PDF exists, trigger compilation
                          if (
                            option.id === "preview" &&
                            currentDocument?.document_type === "latex" &&
                            !compiledPdfBlob &&
                            content.trim()
                          ) {
                            handleCompile();
                          }
                        }}
                        className={clsx(
                          "flex items-center justify-center px-2 sm:px-3 py-2 sm:py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors flex-1 sm:flex-initial",
                          viewMode === option.id
                            ? "bg-gradient-to-r from-teal-600 to-cyan-600 dark:from-teal-500 dark:to-cyan-500 text-white shadow-sm"
                            : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                        )}
                      >
                        <option.icon className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">{option.label}</span>
                      </button>
                    ))}
                </div>

                {/* Compile button - always visible for LaTeX */}
                {currentDocument?.document_type === "latex" && (
                  <div className="flex items-center gap-1.5 border-l border-gray-300 dark:border-gray-600 pl-2 ml-1">
                    {/* Status Indicator */}
                    {compiledPdfBlob && (
                      <>
                        {isPdfStale && isPdfForDifferentFile ? (
                          <div className="relative group">
                            <svg
                              className="w-4 h-4 flex-shrink-0 text-yellow-600 dark:text-yellow-400 cursor-pointer"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              role="img"
                              aria-label="Preview is for a different file"
                            >
                              <path
                                fillRule="evenodd"
                                d="M8.257 3.099c.765-1.36 2.72-1.36 3.485 0l6.516 11.59c.75 1.334-.214 2.99-1.742 2.99H3.483c-1.528 0-2.492-1.656-1.742-2.99l6.516-11.59zM10 7a1 1 0 00-1 1v3a1 1 0 102 0V8a1 1 0 00-1-1zm0 8a1.25 1.25 0 100-2.5A1.25 1.25 0 0010 15z"
                                clipRule="evenodd"
                              />
                            </svg>
                            {/* Custom tooltip that appears instantly on hover */}
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1.5 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-75 pointer-events-none z-50">
                              Preview is for a different LaTeX file. Click Compile to update for this file.
                              {/* Tooltip arrow */}
                              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                            </div>
                          </div>
                        ) : (
                          <div
                            className={clsx(
                              "w-2 h-2 rounded-full flex-shrink-0",
                              isPdfStale
                                ? "bg-yellow-500 dark:bg-yellow-400"
                                : "bg-green-500 dark:bg-green-400"
                            )}
                            title={
                              isPdfStale
                                ? "Preview is outdated. Click Compile to regenerate."
                                : "Preview is up to date"
                            }
                          />
                        )}
                      </>
                    )}

                    <button
                      onClick={() => handleCompile()}
                      disabled={isCompiling || !content.trim()}
                      className={clsx(
                        "flex items-center justify-center px-3 sm:px-2.5 py-2 sm:py-1.5 text-xs font-medium rounded-lg transition-colors min-w-[80px] sm:min-w-0",
                        isCompiling || !content.trim()
                          ? "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                          : "bg-green-600 dark:bg-green-500 text-white hover:bg-green-700 dark:hover:bg-green-600 shadow-sm hover:shadow-md"
                      )}
                      title="Compile LaTeX to PDF"
                    >
                      {isCompiling ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin sm:mr-1.5" />
                          <span className="hidden sm:inline">Compiling...</span>
                        </>
                      ) : (
                        <>
                          <PlayIcon className="w-4 h-4 sm:w-3.5 sm:h-3.5 sm:mr-1.5" />
                          <span className="hidden sm:inline">Compile</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
            {/* secondary editor footer ends */}
            {/* Status Bar begins*/}
            <div className="flex items-center justify-between border-t border-gray-200 bg-gray-100 px-4 py-2 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
              <div className="flex items-center space-x-4">
                <span>Mode: {mode}</span>
                <span>Line 1, Column 1</span>
                {isDirty && !isSaving && (
                  <span className="text-orange-500">
                    • Changes pending save
                  </span>
                )}
                {isSaving && <span className="text-blue-500">• Saving...</span>}
                {/* WebSocket connection status and user presence */}
                {documentId && (
                  <div className="flex items-center gap-3">
                    <span
                      className={clsx(
                        "flex items-center gap-1",
                        isWsConnected
                          ? "text-green-600"
                          : isWsConnecting
                            ? "text-yellow-600"
                            : "text-gray-400"
                      )}
                    >
                      <span
                        className={clsx(
                          "w-2 h-2 rounded-full",
                          isWsConnected
                            ? "bg-green-500"
                            : isWsConnecting
                              ? "bg-yellow-500"
                              : "bg-gray-400"
                        )}
                      />
                      {isWsConnected
                        ? "Live"
                        : isWsConnecting
                          ? "Connecting..."
                          : "Offline"}
                    </span>
                    {/* User presence indicators */}
                    {isWsConnected && wsActiveUsers.length > 0 && (
                      <UserPresenceIndicator
                        users={wsActiveUsers}
                        currentUserId={currentUser?.id}
                      />
                    )}
                  </div>
                )}
                {/* Git Blame Toggle */}
                {currentDocument && (
                  <button
                    onClick={() => setBlameEnabled(!blameEnabled)}
                    className={clsx(
                      "flex items-center px-2 py-1 text-xs font-medium rounded transition-colors",
                      blameEnabled
                        ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50"
                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                    )}
                    title={
                      blameEnabled
                        ? "Disable git blame (hover to see author)"
                        : "Enable git blame (hover to see author)"
                    }
                  >
                    <CodeBracketIcon className="w-3 h-3 mr-1" />
                    Blame
                  </button>
                )}
              </div>
              <div className="flex items-center space-x-4">
                <span>UTF-8</span>
                <span>AI Ready</span>
                <div className="flex items-center space-x-4 text-sm">
                  <span>
                    {content.split(/\s+/).filter((w) => w.length > 0).length}{" "}
                    words
                  </span>
                  <span>{content.length} characters</span>
                  {documentTokenUsage &&
                    documentTokenUsage.total_tokens > 0 && (
                      <span
                        className="text-purple-600 dark:text-purple-400"
                        title="LLM tokens used in this document"
                      >
                        {documentTokenUsage.total_tokens.toLocaleString()}{" "}
                        tokens
                      </span>
                    )}
                  {selection && (
                    <span className="text-primary-600">
                      {selection.text.split(/\s+/).length} selected
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Panel>
      </PanelGroup>

      {/* Share Modal */}
      {
        currentDocument && (
          <ShareDocumentModal
            documentId={currentDocument.id}
            isOpen={shareModalOpen}
            onClose={() => setShareModalOpen(false)}
            onShareUpdate={() => {
              // Refresh if needed
            }}
          />
        )
      }
    </div >
  );
};

export default EditorPage;
